import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as CloseIcon } from '../../assets/eva_close-outline_red.svg';
import { ReactComponent as SaveIcon } from '../../assets/mingcute_check-fill.svg';
import AsyncAutoComplete from 'components/Inputs/ResourceMapping/AsyncAutocomplete';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import {
    Box,
    TextField,
    Typography,
    Button,
    FormControlLabel,
    FormHelperText,
    SelectChangeEvent,
    debounce,
    Stack,
    FormControl,
    Checkbox,
    Select,
    MenuItem,
    Switch,
    Dialog,
    Link,
    Grid,
    Divider,
    Tooltip,
} from '@mui/material';
import { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import RowTypography from 'components/Table/RowTypography';
import eventEmitter from 'SnackBarEventEmitter';
import { editDataPipelineProject, getProgramSectorList, getProjectById, getProjectListByProgram, fetchJobListSLAConfig } from 'service/service';
import CustomTextField from 'components/Inputs/CustomTextField';
import CustomTimePicker from 'components/Inputs/CustomTimePicker';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import SLAConfigurationModal from './SLAConfigurationModal';
import { DataPipelineModal } from './modal-component';
import moment from 'moment';
import info from '../../assets/info-icon.svg';
import infoDim from '../../assets/info-icon-dim.svg'
import IdleFilterBlueIcon from '../../assets/filter_idel_blue.svg';
import { parseFilter, setRdgTableHeight, getAutocompleteCommonComponentsProps, getCommonCustomTextFieldProps, getInputCustomCommonSx } from './DataPipelineHelper';
import { CustomDeleteIcon, CustomEditIcon, CustomArrowIcon } from 'components/CommonIcons/Icons';
import LoaderComponent from 'components/Loader';

export interface TableRow {
    id: number;
    isNew: boolean;
    isEditing: boolean;
    PepwiseProject: string;
    SLAType: string;
    Days: string;
    SLADate: string;
    SLATime?: string | Date;
    SLACriteria?: string;
    SLAAggregation?: string;
    rule?: string;
    jobId?: string;
    jobName?: string;
    projectId?: string;
    projectName?: string;
    platformName?: string;
    schedulerName?: string;
    programId?: string;
    programName?: string;
    scheduleType?: string;
    deadlineType?: string;
    originalData?: TableRow;
}

type DeadlineSlot = {
    dayName: string;
    startTime: string;
    duration: string;
    isCritical: boolean;
    endTime: string;
};

interface Project {
    Project_Id: number;
    Project_Name: string;
    Project_Description: string;
    Project_Owner: string;
    Sectors: string;
    Friendly_Name: string;
    sectorIds?: string[];
    Is_Sla_Configured?: boolean | number;
    Clarity_Project_Id?: string;
    SLA_Date?: string | null;
    SLA_Time?: string | number | null;
    slaType?: number;
    slaData?: any[];
    projectId?: number | string;
    projectName?: string;
    jobId?: string;
    jobName?: string;
    platformName?: string;
    schedulerName?: string;
    programId?: any;
    programName?: any;
}
interface Option {
    value: string;
    label: string;
}

function getSlaDateValue(slaDate: any) {
    if (slaDate === 0 || slaDate === 1) return String(slaDate);
    if (slaDate === "0" || slaDate === "1") return slaDate
    if (!slaDate) return '';
    // If already a number (0 or 1), just return as string
    if (typeof slaDate === 'number') return slaDate.toString();
    // If string, compare to today/tomorrow
    const today = new Date();
    const date = new Date(slaDate);
    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const dayDiff = (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    if (dayDiff === 0) return '0';
    if (dayDiff === 1) return '1';
    return '';
}

function getSlaTimeValue(slaTime: any) {
    if (!slaTime) return '';
    const padded = slaTime.toString().padStart(4, '0');
    return `${padded.slice(0, 2)}:${padded.slice(2)}`;
}

function normalizeScheduleType(value: string): string {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'weekly') return 'Weekly';
    if (normalized === 'daily') return 'Daily';
    return '';
}

interface ProjectQueryParams {
    filterInfo?: any;
    currentFilterType?: string;
    page?: number;
    pageSize?: number;
    stDate?: string;
    edDate?: string;
    sortingInfo?: any;
    sectorId?: number;
    projName?: string;
}

function getSlaVrtnValue(criteria: string | undefined): number | null {
    if (criteria === "All Jobs Completed in Last 24 Hrs") return -1;
    if (criteria === "All Jobs Completed on Same Day") return 0;
    return null;
}

function getSlaAggValue(jobSla: string | undefined): number | null {
    if (jobSla === "JOB_SLA_AGG") return 1;
    return 0;
}

function sanitizeSlaTime(time: string): string {
    if (/^\d{2}:\d{2}$/.test(time)) {
        return time.replace(':', '');
    }
    const digits = time.replace(/\D/g, '');
    if (digits.length === 4) return digits;
    if (digits.length < 4) return digits.padStart(4, '0');
    return digits.slice(-4);
}

const parseDeadlineSlots = (deadline: any): DeadlineSlot[] => {
    const apiDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    if (!deadline || !deadline.slots || typeof deadline.slots !== 'object') return [];
    return Object.keys(deadline.slots)
        .flatMap((dayKey) => {
            const dayIndex = Number(dayKey);
            const dayName = apiDayNames[dayIndex] ?? dayKey;
            const slotGroup = deadline.slots[dayKey];
            if (!slotGroup || typeof slotGroup !== 'object') return [];
            return Object.values(slotGroup)
                .map((slotEntry: any) => ({
                    dayName,
                    startTime: slotEntry.startTime || '',
                    duration: slotEntry.duration !== undefined && slotEntry.duration !== null ? `${Number(slotEntry.duration)} Mins` : '',
                    isCritical: slotEntry.isCritical === 'Y',
                    endTime: slotEntry.endTime || ''
                }));
        })
        .filter((entry): entry is DeadlineSlot => Boolean(entry && entry.startTime));
};

const mergeDailySlots = (slots: DeadlineSlot[]): DeadlineSlot[] => {
    const grouped: Record<string, DeadlineSlot & { days: string[] }> = {};
    const apiDayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    slots.forEach((slot) => {
        const key = `${slot.startTime}|${slot.endTime}|${slot.duration}|${slot.isCritical}`;
        if (!grouped[key]) {
            grouped[key] = { ...slot, days: [slot.dayName] };
        } else if (!grouped[key].days.includes(slot.dayName)) {
            grouped[key].days.push(slot.dayName);
        }
    });

    return Object.values(grouped).map((slot) => ({
        ...slot,
        days: apiDayOrder.filter((day) => slot.days.includes(day))
    }));
};

function timeStringToDate(timeStr: string | Date | undefined | null): Date | null {
    if (!timeStr) return null;
    if (timeStr instanceof Date) return timeStr; // Handle Date objects safely
    if (typeof timeStr !== 'string') return null; // Guard: only process strings below

    // Accepts "HH:mm" or "YYYY-MM-DDTHH:mm"
    let [hours, minutes] = [0, 0];
    if (timeStr.includes('T')) {
        // "YYYY-MM-DDTHH:mm"
        const parts = timeStr.split('T')[1]?.split(':');
        if (parts && parts.length >= 2) {
            hours = Number.parseInt(parts[0], 10);
            minutes = Number.parseInt(parts[1], 10);
        }
    } else if (timeStr.includes(':')) {
        // "HH:mm"
        const parts = timeStr.split(':');
        if (parts.length >= 2) {
            hours = Number.parseInt(parts[0], 10);
            minutes = Number.parseInt(parts[1], 10);
        }
    }
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
}

export const getSLAGridCustomSx = () => ({
    '& .rdg .rdg-cell': {
        fontWeight: 500,
        padding: '9px 7px 4px 10px !important',
        color: '#000',
        lineHeight: '28px',
        fontSize: '14px'
    },
    '& .rdg-cell': {
        borderInlineEnd: '1px solid #fff !important',
        borderBlockEnd: '1px solid #fff !important',
        paddingTop: '16px !important',
        paddingBottom: '16px !important',
    },
    '& .rdg-header-row .rdg-cell': {
        padding: '4px 7px 4px 10px !important',
        borderInlineEnd: '1px solid #fff !important',
        borderBlockEnd: '1px solid #fff !important',
    },
    '& .rdg-row': {
        borderBlockEnd: '1px solid #fff !important',
    },
});

export const SLATab = ({ programData, programID, programName, setProjectData}: any) => {  
    const theme = useTheme(); 
    const colors = theme.palette;  
    const commonCustomTextFieldProps = getCommonCustomTextFieldProps(colors);
    useEffect(() => {
        document.body.classList.add("v2Background");
    },);
    const [sectorList, setSectorList] = useState<any[]>([]);
    const [loading, setLoading] = useState<any>({
        sector: false,
        project: false
    });
    const inputValue = '';
    const [sectorName, setSectorName] = useState('all');
    const startDate = moment().tz("America/Chicago").startOf('day').format('YYYY-MM-DD');
    const endDate = moment().tz("America/Chicago").endOf('day').format('YYYY-MM-DD');
    const [tableLoading, setTableLoading] = useState(false);
    const [jobLevelTableLoading, setJobLevelTableLoading] = useState(false);
    const sortingInfo = { sortBy: '', sortDir: '' };
    const [filterInfo, setFilterInfo] = useState([]);
    const currentFilterType = '';
    const sectorMasterList: any[] = [];
    const [projectList, setProjectList] = useState([]);
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');
    const [dropdownLoading, setDropdownLoading] = useState<any>({
        project: false,
        owner: false
    });

    const [, setFormData] = useState<any>({
        id: '',
        Sectors: [],
        ProgramName: '',
        projectName: '',
        projectDescription: '',
        projectOwner: '',
        clarityId: '',
        slaDate: 0,
        slaTime: '',
        reqSlaTime: '',
        friendlyName: '',
        isSlaConfigured: true,
        slaType: '',
        slaData: []
    });

    //dropdown
    const [selectedValue, setSelectedValue] = useState("daily");

    const validationInitialState = {
        project: false,
    };

    const [selectedRows, setSelectedRows] = useState<TableRow[]>([]);
    const [jobRows, setJobRows] = useState<TableRow[]>([]);
    const [isAdminUser, setIsAdminUser] = useState(false);

    const SLA_MODAL_STATE_KEY = 'SLAConfigurationModalState';
    const [configModalProject, setConfigModalProject] = useState<Project | null>(null);
    const [configModalInitialData, setConfigModalInitialData] = useState<any>(null);
    const [isSLAConfigModalOpen, setIsSLAConfigModalOpen] = useState(false);
    const [isSLAConfigViewOnly, setIsSLAConfigViewOnly] = useState(false);
    const isNewAllocRowExists = selectedRows && selectedRows.some((row) => row.isNew || row.isEditing === true);

    const persistSlaModalState = (state: any) => {
        try {
            sessionStorage.setItem(SLA_MODAL_STATE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('Unable to persist SLA modal state', e);
        }
    };

    const clearSlaModalState = () => {
        try {
            sessionStorage.removeItem(SLA_MODAL_STATE_KEY);
        } catch (e) {
            console.error('Unable to clear SLA modal state', e);
        }
    };

    useEffect(() => {
        try {
            const stored = sessionStorage.getItem(SLA_MODAL_STATE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed?.open && parsed.selectedProject && parsed.initialData && !parsed.viewOnly) {
                    setConfigModalProject(parsed.selectedProject);
                    setConfigModalInitialData(parsed.initialData); 
                    setIsSLAConfigModalOpen(true);
                    setIsSLAConfigViewOnly(false);
                } else if (parsed?.viewOnly) {
                    clearSlaModalState();
                }
            }
        } catch (e) {
            console.error('Failed to restore persisted SLA modal state', e);
        }
    }, []);
    
    // Job Level SLA States
    const [jobLevelProjects, setJobLevelProjects] = useState<any[]>([]);
    const [jobLevelSLAData, setJobLevelSLAData] = useState<any>(null);

    const defaultColumnNamesSLA = [
        'Projects',
        'SLA Type',
        'Days',
        'SLA Date',
        'SLA Time (CST)',
        'SLA Criteria',
        'SLA Aggregation',
        'Actions'
    ];

    const columnNames = [
        { name: 'PepwiseProject', label: 'Projects', isEditable: true },
        { name: 'SLAType', label: 'SLA Type', isEditable: false },
        { name: 'Days', label: 'Days', isEditable: false },
        { name: 'SLADate', label: 'SLA Date', isEditable: false },
        { name: 'SLATime', label: 'SLA Time (CST)', isEditable: false },
        { name: 'SLACriteria', label: 'SLA Criteria', isEditable: true },
        { name: 'SLAAggregation', label: 'SLA Aggregation', isEditable: true },
        { name: 'actions', label: 'Actions', isEditable: false }
    ];
    const [validationErrors, setValidationErrors] = useState(validationInitialState);

    const [newAllocation, setNewAllocation] = useState<TableRow>({
        id: 0,
        isNew: true,
        PepwiseProject: '',
        SLAType: '',
        Days: '',
        SLADate: '0',
        SLATime: '',
        isEditing: false
    });

    const handleOpenSLAConfigModal = (row: TableRow, viewOnly: boolean = false) => {
        setIsSLAConfigViewOnly(viewOnly);
        
        const selectedProject = getSelectedProject(row);
        
        let projectId = row.projectId;
        let jobId = row.jobId;
        let jobName = row.jobName;
        let platformName = row.platformName;
        let schedulerName = row.schedulerName;
        
        if ((!projectId || !jobId || !platformName || !schedulerName) && jobLevelSLAData?.filterOptions) {
            const foundProject = jobLevelSLAData.filterOptions.find((p: any) => {
                return p.projectName === row.PepwiseProject;
            });
            
            if (foundProject) {
                projectId = foundProject.projectId;
                
                // Find the job in the project's jobs array
                const foundJob = foundProject.jobs?.find((j: any) => {
                    return j.jobName === row.jobName;
                });
                if (foundJob) {
                    jobId = foundJob.jobId;
                    jobName = foundJob.jobName;
                    platformName = foundJob.platformName;
                    schedulerName = foundJob.schedulerName;
                } 
            } 
        }
        
        const project = selectedProject ? {
            ...selectedProject,
            projectId: projectId || selectedProject.Project_Id,
            schedulerName: schedulerName,
            projectName: selectedProject.Project_Name,
            jobId: jobId,
            jobName: jobName,
            platformName: platformName,
            programId: programID,
            programName: programName
        } : {
            Project_Id: 0,
            Project_Name: row.PepwiseProject,
            Project_Description: '',
            Project_Owner: '',
            Sectors: '',
            Friendly_Name: '',
            sectorIds: [],
            projectId: projectId,
            projectName: row.PepwiseProject,
            jobId: jobId,
            jobName: jobName,
            platformName: platformName,
            schedulerName: schedulerName,
            programId: programID,
            programName: programName
        };
        const initialData = {
            isNew: true,
            scheduleType: '',
            deadlineType: 'Clock',
            rule: '',
            slotsAvailable: 'Yes',
            duration: '',
            slaStartTimes: [
                {
                    id: 1,
                    days: [],
                    startTime: '',
                    endTime: '',
                    duration: '',
                    criticality: true
                }
            ]
        };

        setConfigModalProject(project);
        setConfigModalInitialData(initialData);
        setIsSLAConfigModalOpen(true);
        persistSlaModalState({ open: true, selectedProject: project, initialData, formData: initialData, viewOnly });
    };

    const handleCloseSLAConfigModal = () => {
        setIsSLAConfigModalOpen(false);
        setConfigModalProject(null);
        setConfigModalInitialData(null);
        clearSlaModalState();
    };

    const handleSaveSLAConfigModal = async (_data: any) => {
        handleCloseSLAConfigModal();
        await getJobLevelSLAConfig();
    };
    const [projectOptions, setProjectOptions] = useState<Option[]>([]);
    const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
    const [editValidationErrors, setEditValidationErrors] = useState(validationInitialState);
    const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });
    const [currentEditRow, setCurrentEditRow] = useState<TableRow>({
        id: 0,
        isNew: false,
        isEditing: true,
        PepwiseProject: '',
        SLAType: '',
        SLADate: '',
        SLATime: '',
        Days: '',
    });

    let sladataStr = searchParams.get('sladata');

    const hasInitialized = useRef(false);
    useEffect(() => {
        if (!hasInitialized.current && sladataStr) {
            try {
                const slaDataObj = JSON.parse(decodeURIComponent(sladataStr));

                const paddedTime = slaDataObj.SLA_Time.toString().padStart(4, '0');
                const hours = paddedTime.slice(0, 2);
                const minutes = paddedTime.slice(2);
                const isoDateStringWeekly = `${slaDataObj.SLA_Date}T${hours}:${minutes}`;

                const slaTypeValue = slaDataObj.SLA_Type === 1 ? 'weekly' : 'daily';
                setSelectedValue(slaTypeValue);
                setFormData((prevData: any) => ({
                    ...prevData,
                    slaType: slaTypeValue,
                    slaDate: slaDataObj.SLA_Date,
                    slaTime: isoDateStringWeekly,
                }));

                hasInitialized.current = true;
            } catch (e) {
                console.error("Failed to parse SLA data:", e);
            }
        }

    }, [searchParams]);

    eventEmitter.on('showSnackbar', (message: string) => {
        setLoading(false);
        setDropdownLoading({
            project: false,
            owner: false
        });
    });


    const debouncedFetchOptionsData = useMemo(
        () =>
            debounce(async (value, type) => {
                setLoading({
                    ...loading,
                    project: true
                });
                if (type === 'projectName') {
                    getProjectList(value);
                } else {
                    getOwnerList(value);
                }
                setLoading({
                    ...loading,
                    project: false
                });
            }, 500),
        []
    );

    useEffect(() => {

        programData.action = 'CREATE';
        programData.isOpen = false;
        getOwnerList(programData?.data?.Project_Owner ? (programData?.data?.Project_Owner)?.split(' ')[0]?.trim() : '');
        getSectorData();

        let sectorId;
        if (sectorName && sectorName != 'all' && sectorMasterList?.length > 0) {
            sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id
        }
        getProgramDetails(sectorId);
    }, [selectedValue]);

    const getJobLevelSLAConfig = async () => {
        try {
            setJobLevelTableLoading(true);
            const requestBody = {
                programId: programID,
            };
            const response = await fetchJobListSLAConfig(requestBody);

            if (response && response.data) {
                setJobLevelSLAData(response.data);

                if (response.data.filterOptions && Array.isArray(response.data.filterOptions)) {
                    const projectsData = response.data.filterOptions.map((item: any) => ({
                        projectId: item.projectId,
                        projectName: item.projectName,
                        jobs: item.jobs || []
                    }));
                    setJobLevelProjects(projectsData);
                }

                setIsAdminUser(response.data.isAdminUser);
                if (response.data.slaList) {
                    const transformedJobRows: TableRow[] = [];
                    let rowIdCounter = Date.now();

                    Object.keys(response.data.slaList).forEach((projectId: string) => {
                        const slaListForProject = response.data.slaList[projectId];

                        if (Array.isArray(slaListForProject)) {
                            slaListForProject.forEach((item: any, index: number) => {
                                const scheduleType = normalizeScheduleType(item.scheduleType || item.slaType || '');
                                const deadline = item.deadline || {};
                                const deadlineType = String(deadline.type || '').toUpperCase() === 'INTERVAL' ? 'Interval' : 'Clock';
                                transformedJobRows.push({
                                    id: rowIdCounter + index,
                                    isNew: false,
                                    isEditing: false,
                                    PepwiseProject: item.projectName || '',
                                    projectId: item.projectId || '',
                                    jobId: item.jobId || '',
                                    jobName: item.jobName || '',
                                    platformName: item.platformName || '',
                                    schedulerName: item.schedulerName ||'',
                                    SLAType: scheduleType,
                                    scheduleType,
                                    deadlineType,
                                    rule: deadline.rule || '',
                                    Days:'',
                                    SLADate: '',
                                    SLATime: '',
                                });
                            });
                        }
                    });

                    setJobRows(transformedJobRows);
                }
            }
        } catch (error) {
            console.error('Error fetching Job Level SLA config:', error);
            eventEmitter.emit('showSnackbar', 'Error loading Job Level SLA configuration');
        } finally {
            setJobLevelTableLoading(false);
        }
    };

    useEffect(() => {
        if (programData.type === 'Project' || type === 'Project') {
            debouncedFetchOptionsData(inputValue, 'projectName');
        }
        let sectorId;
        if (sectorName && sectorName != 'all' && sectorMasterList?.length > 0) {
            sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id
        }
        getProjectsByProgramData({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId});
    }, [inputValue, debouncedFetchOptionsData]);

    // Load job level SLA config on component mount
    useEffect(() => {
        if (programID) {
            getJobLevelSLAConfig();
        }
    }, [programID]);


    const getSectorNameId = () => {
        let sectorId;
        let selectedSctr = localStorage.getItem('selectedSector')
        if (selectedSctr && selectedSctr != 'all') {
            setSectorName(selectedSctr)
            const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '')
            if (sectorList) {
                sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id
            }
        }
        return sectorId;
    }

    const getProjectsByProgramData = async ({ 
        filterInfo, 
        currentFilterType, 
        page, 
        pageSize, 
        stDate, 
        edDate, 
        sortingInfo, 
        sectorId, 
        projName 
    }: ProjectQueryParams) => {
        setTableLoading(true);
        stDate = stDate || startDate
        edDate = edDate || endDate
        sectorId = sectorId || getSectorNameId()
        let result = await getProjectListByProgram(programID, page, pageSize, currentFilterType, filterInfo, stDate, edDate, sortingInfo, sectorId, projName || '');
        setTimeout(() => {
            setRdgTableHeight(
                `project_list${programID}`,
                result.data.projectList.length
            );
        }, 100);
        setTableLoading(false);
        const projData = result?.data?.projectList?.map((list: any) => {
            list['activeRow'] = 0;
            const selectedSectors = list.Sectors && list?.Sectors?.split(', ');
            list['Sectors'] = selectedSectors && selectedSectors.length >= 10 ? ['All 10 Sectors'] : list.Sectors;
            // Convert Is_Sla_Configured based on isV2
            list.Is_Sla_Configured = Boolean(list.Is_Sla_Configured);
            return list;
        });
        const filtersData: any = {
            Sectors: [], Project_Name: [], SLA_Status: []
        }
        if (result.data.filterOptions) {
            for (const [key, value] of Object.entries(result.data.filterOptions)) {
                if (key === 'sector' || key === 'Sector_Name') {
                    filtersData['Sectors'] = value;
                } 
                else if (key === 'Project_Name' || key === 'projectName') {
                    filtersData['Project_Name'] = value;
                } 
                else if (key === 'SLA_Status' || key === 'projectSlaStatus') {
                    filtersData['SLA_Status'] = (value as string[]).filter(Boolean); 
                }
            }
        }
        setFilterInfo(filterInfo)
        setProjectList(projData);
        setProjectData(projData);

        const slaConfiguredProjects = projData && projData?.filter(
            (item: any) => item.Is_Sla_Configured === 1 || item.Is_Sla_Configured === true
        );

        const transformedData = slaConfiguredProjects?.flatMap((item: any) => {
            let slaCriteriaValueUpdate = 'Null';
            let slaAggregationValueUpdate = 'Null';
            if (item.sla_vrtn === -1) slaCriteriaValueUpdate = 'All Jobs Completed in Last 24 Hrs';
            else if (item.sla_vrtn === 0) slaCriteriaValueUpdate = 'All Jobs Completed on Same Day';
            slaAggregationValueUpdate = 'PROJECT_SLA_AGG';
            if (item?.sla_agg === 1)
                slaAggregationValueUpdate = 'JOB_SLA_AGG';

            if (Array.isArray(item.slaData) && item.slaData.length > 0) {
                // Only include weekly rows with valid time and days
                return item.slaData.filter((sla: any) =>
                    sla.SLA_Type === 1 && sla.SLA_Time !== null && sla.SLA_Time !== undefined &&
                    sla.SLA_Week_Days && sla.SLA_Week_Days !== '').map((sla: any, idx: number) => {
                        let slaCriteriaValue = '';
                        if (sla.sla_vrtn === -1) slaCriteriaValue = 'All Jobs Completed in Last 24 Hrs';
                        else if (sla.sla_vrtn === 0) slaCriteriaValue = 'All Jobs Completed on Same Day';

                        let slaAggregationValue = 'PROJECT_SLA_AGG';
                        if (sla.sla_agg === 1) slaAggregationValue = 'JOB_SLA_AGG';

                        return {
                            ...item,
                            slaData: [sla],
                            id: `${item.Project_Id}_${sla.Sla_Config_Id || idx}`,
                            PepwiseProject: item.Project_Name,
                            SLAType: 'Weekly',
                            Days: sla.SLA_Week_Days,
                            SLADate: sla.SLA_Date,
                            slaDateValue: getSlaDateValue(sla.SLA_Date),
                            SLATime: getSlaTimeValue(sla.SLA_Time),
                            SLACriteria: slaCriteriaValue,
                            SLAAggregation: slaAggregationValue,
                        };
                    });
                } else if (item.slaType === 0 && item.SLA_Time !== null && item.SLA_Time !== undefined ) {
                    // Only include daily row if valid time
                    return [{
                        ...item,
                        id: `${item.Project_Id}_single`,
                        PepwiseProject: item.Project_Name,
                        SLAType: 'Daily',
                        Days: 'All Days',
                        SLADate: item.SLA_Date,
                        slaDateValue: getSlaDateValue(item.SLA_Date),
                        SLATime: getSlaTimeValue(item.SLA_Time),
                        SLACriteria: slaCriteriaValueUpdate,
                        SLAAggregation: slaAggregationValueUpdate,
                    }];
                } else {
                    // No valid SLA rows for this project
                    return [];
                }
            });
        setSelectedRows(transformedData);

        // project dropdown options
        const options: Option[] = projData?.map((item: any) => ({
            value: item.Project_Id,
            label: item.Project_Name
        }));
        setProjectOptions(options);

        // default project match
        const matchedProject = projData?.find((proj: any) =>
            options.some((opt: any) => opt.label === proj.Project_Name)
        );

        const slaType = matchedProject?.slaType === 0 ? 'Daily' : 'Weekly';
        let days = 'All Days';
        if (slaType === 'Weekly' && matchedProject?.slaData?.length) {
            days = matchedProject.slaData.map((d: any) => d.SLA_Week_Days).join(', ');
        }

        let slaCriteriaValue = 'Null';
        if (matchedProject?.sla_vrtn === -1) slaCriteriaValue = 'All Jobs Completed in Last 24 Hrs';
        else if (matchedProject?.sla_vrtn === 0) slaCriteriaValue = 'All Jobs Completed on Same Day';

        let slaAggregationValue = 'PROJECT_SLA_AGG';
        if (matchedProject?.sla_agg === 1) slaAggregationValue = 'JOB_SLA_AGG';

        setNewAllocation((prev) => {
            // SLA Date: convert to 0 or 1
            let slaDateValue = '';
            if (matchedProject?.SLA_Date) {
                const today = new Date();
                const slaDate = new Date(matchedProject.SLA_Date);
                slaDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                const dayDiff = (slaDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
                if (dayDiff === 0) slaDateValue = '0';
                else if (dayDiff === 1) slaDateValue = '1';
            }
            // SLA Time: format as HH:mm if present
            let slaTimeValue = '';
            if (matchedProject?.SLA_Time) {
                const padded = matchedProject.SLA_Time.toString().padStart(4, '0');
                slaTimeValue = `${padded.slice(0, 2)}:${padded.slice(2)}`;
            } else if (matchedProject?.slaData?.length && matchedProject.slaData[0].SLA_Time) {
                const padded = matchedProject.slaData[0].SLA_Time.toString().padStart(4, '0');
                slaTimeValue = `${padded.slice(0, 2)}:${padded.slice(2)}`;
            }
            return {
                ...prev,
                id: matchedProject?.Project_Id,
                PepwiseProject: matchedProject?.Project_Name || '',
                SLAType: slaType,
                Days: days,
                SLADate: slaDateValue,
                SLATime: slaTimeValue,
                SLACriteria: slaCriteriaValue,
                SLAAggregation: slaAggregationValue
            };
        });
    };

    const getOwnerList = async (name = '') => {
        setDropdownLoading({
            ...dropdownLoading,
            owner: true
        });
        setDropdownLoading({
            ...dropdownLoading,
            owner: false
        });
    };

    const getProjectList = async (name = '') => {
        setDropdownLoading({
            ...dropdownLoading,
            project: true
        });
        setDropdownLoading({
            ...dropdownLoading,
            project: false
        });
    };



    const getProgramDetails = async (sectorId?: number, date?: string) => {
        setLoading(true);
        setLoading(false);
    };

    const getSectorData = async () => {
        setLoading({
            ...loading,
            sector: true
        });
        let sectors: any = await getProgramSectorList();
        setSectorList(sectors.data.sectors);
        setLoading({
            ...loading,
            sector: false
        });
    };

    const getProjectDetailsForSLAData = async (projectId: number, programId: number) => {
        let commonDate = moment(startDate).format('YYYY-MM-DD');
        let result = await getProjectById({ projectId, programId, commonDate });
        return result;
    };

    const getProjectDetails = () => {
        let slaDataObj;
        if (sladataStr) {
            slaDataObj = JSON.parse(decodeURIComponent(sladataStr));
        }

        function formatSlaTime(time: any) {
            if (time === undefined || time === null) return 'NA';
            if (time === 0) return '00:00';
            if (typeof time === 'number') {
                if (time < 10) return `000${time}`.replace(/(.{2})$/, ':$1');
                if (time < 100) return `00${time}`.replace(/(.{2})$/, ':$1');
                return time.toString().replace(/(.{2})$/, ':$1');
            }
            return 'NA';
        }
        let reqslatime = 'NA';
        if (programData.data?.slaTime !== undefined && programData.data?.slaTime !== null) {
            reqslatime = formatSlaTime(programData.data?.slaTime);
        } else if (programData.data?.SLA_Time !== undefined && programData.data?.SLA_Time !== null) {
            reqslatime = formatSlaTime(programData.data?.SLA_Time);
        }
        let days: any = []
        if (programData.data?.slaType == 1) {
            programData.data?.slaData.length && programData.data?.slaData.map((obj: any, index: number) => {
                let weekdays = obj.SLA_Week_Days.split(",").map((day: any) => day.trim())
                days.push(...days, ...weekdays)
            })
        }
        else if (slaDataObj?.SLA_Type == 1) {
            const weekdays = slaDataObj?.SLA_Week_Days.split(',').map((day: string) => day.trim());
            days.push(...weekdays);
        }


        setFormData({
            projectName: programData.data?.Project_Name,
            projectDescription: programData.data?.Project_Description,
            projectOwner: programData.data?.Project_Owner,
            projectId: programData.data?.Project_Id,
            Sectors: programData.data?.Sectors ? programData.data.Sectors.split(', ') : [],
            friendlyName: programData.data?.Friendly_Name,
            slaTime: programData.data?.slaType == 1 ? null : `2024-07-21T${reqslatime}`,
            slaDate: programData.data?.slaType == 1 ? 0 : programData.data?.dateValue || 0,
            reqSlaTime: programData.data?.slaType == 1 ? null : programData.data?.SLA_Time,
            slaType: (programData.data?.slaType == 1 || slaDataObj?.SLA_Type == 1) ? 'weekly' : 'daily',
            isSlaConfigured: programData.data?.Is_Sla_Configured == 1 || programData.data?.Is_Sla_Configured
        });
    };

    useEffect(() => {

        if (programData.type === 'Program') {
            if (programData.action === 'EDIT') {
                getProgramDetails();
            }
        } else if (programData.type === 'Project') {
            if (programData.action === 'EDIT') {
                getProjectDetails();
            }
            getOwnerList(programData?.data?.Project_Owner ? (programData?.data?.Project_Owner)?.split(' ')[0]?.trim() : '');
            getSectorData();
        }
        if (programData.action == 'CREATE' || programData.action == 'EDIT') {
            getProjectDetails();
        }
    }, []);


    const jobLevelGridRef = useRef<any>();
    const jobLevelScrollTop = useRef<number>(0);
    const prevJobRowsLength = useRef(jobRows.length);

    useLayoutEffect(() => {
        const gridEl = jobLevelGridRef.current?.element;
        if (!gridEl) return;

        gridEl.scrollTop = jobLevelScrollTop.current;

        if (!gridEl.dataset.scrollListenerAttached) {
            gridEl.dataset.scrollListenerAttached = 'true';
            gridEl.addEventListener('scroll', () => {
                jobLevelScrollTop.current = gridEl.scrollTop;
            });
        }
    });

    useEffect(() => {
        const gridEl = jobLevelGridRef.current?.element;
        if (!gridEl) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                gridEl.scrollTop = jobLevelScrollTop.current;
            });
        });
    }, [jobRows]);

    // Scroll to new row when one is added, and sync the ref so the layout effect above doesn't undo it
    useEffect(() => {
        const rowCountIncreased = jobRows.length > prevJobRowsLength.current;
        prevJobRowsLength.current = jobRows.length;

        if (!rowCountIncreased) return;

        const newRowIdx = jobRows.findIndex(row => row.isNew);
        if (newRowIdx === -1 || !jobLevelGridRef.current) return;

        setTimeout(() => {
            try {
                jobLevelGridRef.current.scrollToRow(newRowIdx);
                const gridEl = jobLevelGridRef.current?.element;
                if (gridEl) {
                    jobLevelScrollTop.current = gridEl.scrollTop; // <-- sync so layout effect doesn't reset it
                }
            } catch (e) {
                console.error(e);
            }
        }, 100);
    }, [jobRows]);

    const gridRef = useRef<any>();
    useEffect(() => {
        // Only scroll if a new row (isNew) exists, not just editing
        const newRowIdx = selectedRows.findIndex(row => row.isNew);
        if (newRowIdx !== -1 && gridRef.current) {
            setTimeout(() => {
            try {
                    gridRef.current.scrollToRow(newRowIdx);
                } catch (e) {
                    console.error(e);
                    }
                }, 100); // Delay to ensure grid has rendered
                }
    }, [selectedRows]);

    const lastEditRowId = useRef<any>(null);
   useEffect(() => {
    // Reset lastEditRowId when no row is in edit mode
    if (!selectedRows.some(r => r.isEditing)) {
        lastEditRowId.current = null;
    }
   }, [selectedRows]);

    const handleAddNewRow = () => {
        setValidationErrors(validationInitialState);

        const newRow = {
            id: Date.now(),
            isNew: true,
            isEditing: true,
            PepwiseProject: '',
            SLAType: '',
            Days: '',
            SLADate: '0',
            SLATime: '',
            SLACriteria: '',
            SLAAggregation: ''
        };
        setSelectedRows([...selectedRows, newRow]);
        setNewAllocation(newRow);
    };

    const handleJobLevelChange = (id: number, field: string, value: any) => {
        const gridEl = jobLevelGridRef.current?.element;
        if (gridEl) {
            jobLevelScrollTop.current = gridEl.scrollTop;
        }
        setJobRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                if (field === 'PepwiseProject') {
                    return { ...row, [field]: value, jobName: '' };
                }
                return { ...row, [field]: value };
            })
        );
    };

    const handleAddNewJobLevelRow = () => {
        setValidationErrors(validationInitialState);

        const newRow = {
            id: Date.now(),
            isNew: true,
            isEditing: false,
            PepwiseProject: '',
            jobName: '',
            SLAType: '',
            Days: '',
            SLADate: '',
            SLATime: '',
            SLAEndTime: '',
            SLACriteria: '',
            SLAAggregation: '',
            rule: '',
        };
        setJobRows((prevRows) => [...prevRows, newRow]);
    };

    const handleDeleteRow = (id: number) => {
        setJobRows((prevRows) => prevRows.filter((row) => row.id !== id));
    };

    const getSectorIds = (sectorList: any[], selectedProject: any): string[] => {
        let sectorIds: string[] = [];
        for (const sector of sectorList) {
            if (
                typeof selectedProject.Sectors === "string" &&
                selectedProject.Sectors
                    .split(",")
                    .map((s: string) => s.trim())
                    .includes(sector.Sector_Name)
            ) {
                sectorIds.push(sector.Sector_Id);
            }
        }
        return sectorIds;
    };

    function getSelectedProject(newRow: TableRow) {
        if (!Array.isArray(projectList) || projectList.length === 0) {
            return undefined;
        }

        return projectList.find(
                (proj: any) => proj.Project_Name === newRow.PepwiseProject
            ) as Project | undefined;
    }

    const handleAddSLA = async () => {
        setTableLoading(true);
        // Find the new row (isNew === true)
        const newRow = selectedRows.find(row => row.isNew);
        if (!newRow) return;

        const selectedProject = getSelectedProject(newRow);
        if (!selectedProject) {
            setTableLoading(false);
            eventEmitter.emit('showSnackbar', 'Please select a valid project.');
            return;
        }

        // Fetch latest project details from backend
        const projectId = selectedProject.Project_Id;
        const programId = programID;
        const result = await getProjectDetailsForSLAData(projectId, programId);

        // Defensive: check if result is valid
        const projectData = result?.data?.programData?.[0];
        if (!projectData) {
            eventEmitter.emit('showSnackbar', 'Failed to fetch project details.');
            return;
        }

        let sectorIds = getSectorIds(sectorList, selectedProject);

        let slaData: any[] = [];
        if (newRow.SLAType === 'Weekly') {
            slaData = selectedRows
            .filter(row =>
                row.PepwiseProject === newRow.PepwiseProject &&
                row.SLAType === 'Weekly'
            )
            .map((row, idx) => ({
                id: idx + 1,
                days: row.Days,
                slaTime: row.SLATime && typeof row.SLATime === 'string'
                ? Number(sanitizeSlaTime(row.SLATime))
                : (() => {
                    let slaTimeValue = 0;
                    if (typeof row.SLATime === 'number') {
                        slaTimeValue = row.SLATime;
                    }
                    return slaTimeValue;
                })(),
                slaDate: row.SLADate === '' ? 0 : Number(row.SLADate),
                slaVrtn: getSlaVrtnValue(row.SLACriteria),
                slaAgg: getSlaAggValue(row.SLAAggregation)
            }));
        }

        let computedSlaTime = 0;
        if (newRow.SLAType === "Weekly") {
            if (slaData.length > 0) {
                computedSlaTime = slaData.at(-1)!.slaTime;
            }
        } else if (typeof newRow.SLATime === "string") {
            computedSlaTime = Number(sanitizeSlaTime(newRow.SLATime));
        } else if (typeof newRow.SLATime === "number") {
            computedSlaTime = newRow.SLATime;
        }
console.log (`before edit payload :  ${newRow ? JSON.stringify(newRow):"empty row"}`)
        // Prepare the payload using backend data
        const data = {
            id: projectData.Project_Id,
            projectOwner: projectData.Project_Owner,
            projectName: projectData.Project_Name,
            projectDescription: projectData.Project_Description,
            mulSector: sectorIds,
            friendlyName: projectData.Friendly_Name,
            isSlaConfigured: true,
            slaType: newRow.SLAType === 'Weekly' ? 1 : 0,
            slaVrtn: getSlaVrtnValue(newRow.SLACriteria),
            slaAgg: getSlaAggValue(newRow.SLAAggregation),
            slaData: slaData,
            slaDate: newRow.SLADate === '' ? 0 : Number(newRow.SLADate),
            slaTime: computedSlaTime,
            isSlaChanged: true
        };

        // Call the API
        try {
            const result = await editDataPipelineProject(data);
            if (result.data?.message) {
                await getProjectsByProgramData({});
            } else if (result.response?.status === 400) {
                eventEmitter.emit('showSnackbar', result.response.data.data.message);
            }
        } catch (e) {
            console.error(e);
            eventEmitter.emit('showSnackbar', 'Failed to save SLA.');
        } finally {
            setTableLoading(false); // Optionally, hide loader if not handled in getProjectsByProgram
        }
    };

    const getUpdatedSlaData = (
        row: any,
        selectedProject: any,
    ): any[] => {
        let updatedSlaData = [];
        if (row.SLAType === 'Weekly' && Array.isArray(selectedProject.slaData)) {
            const rowIdStr = String(row.id);
            let rowSlaConfigId: string | number | null = null;
            rowSlaConfigId = rowIdStr.includes('_')
            ? rowIdStr.split('_')[1]
            : null;
            updatedSlaData = selectedProject.slaData.filter((sla: any) => {
                if (rowSlaConfigId && sla.Sla_Config_Id) {
                    if (sla.Sla_Config_Id == rowSlaConfigId) {
                        sla['isDeleted'] = true
                        return true
                    } else {
                        return sla.Sla_Config_Id !== rowSlaConfigId;
                    }

                }
                return sla.SLA_Week_Days !== row.Days;
            });
        }

        if (row.SLAType === 'Daily') {
            updatedSlaData = [];
        }
        return updatedSlaData;
    };

    const handleDeleteExistingRow = async (row: TableRow) => {
        setTableLoading(true);
        try {
            // Find the selected project from projectList
            const selectedProject = projectList.find(
                (proj: any) => proj.Project_Name === row.PepwiseProject
            ) as Project | undefined;

            if (!selectedProject) {
                eventEmitter.emit('showSnackbar', 'Project not found.');
                setTableLoading(false);
                return;
            }

            let sectorIds = getSectorIds(sectorList, selectedProject);
            // Remove only the selected SLA entry
            let updatedSlaData = getUpdatedSlaData(row, selectedProject);

            // --- FIX: Map to only required fields and filter out incomplete rows ---
            updatedSlaData = updatedSlaData
                .map((sla: any) => ({
                    days: sla.SLA_Week_Days,
                    slaDate: sla.SLA_Date,
                    slaTime: sla.SLA_Time,
                    isDeleted: sla.isDeleted ? sla.isDeleted : false,
                    slaVrtn: getSlaVrtnValue(sla.SLACriteria),
                    slaAgg: getSlaAggValue(sla.SLAAggregation)
                }))
                .filter(
                    (sla: any) =>
                        sla?.days !== undefined &&
                        sla?.days !== null &&
                        sla?.days !== '' &&
                        sla?.slaTime !== undefined &&
                        sla?.slaTime !== null &&
                        sla?.slaDate !== undefined &&
                        sla?.slaDate !== null
                );

            const isWeekly = row.SLAType === 'Weekly';
            const isWeeklySla = updatedSlaData.length > 0;
            const isDailySla = row.SLAType === 'Daily' && selectedProject.SLA_Time !== null && selectedProject.SLA_Time !== undefined;
            const isConfigured = isWeekly ? isWeeklySla : isDailySla;

            const data = {
                id: selectedProject.Project_Id,
                projectOwner: selectedProject.Project_Owner,
                projectName: selectedProject.Project_Name,
                projectDescription: selectedProject.Project_Description,
                mulSector: sectorIds,
                friendlyName: selectedProject.Friendly_Name,
                isSlaConfigured: isConfigured,
                slaType: updatedSlaData.length > 0 ? 1 : 0,
                slaData: updatedSlaData,
                slaDate: null,
                slaTime: null,
                isSlaChanged: true,
                slaVrtn: getSlaVrtnValue(row.SLACriteria),
                slaAgg: getSlaAggValue(row.SLAAggregation)
            };

            const result = await editDataPipelineProject(data);
            if (result.data?.message) {
                await getProjectsByProgramData({});
            } else if (result.response?.status === 400) {
                eventEmitter.emit('showSnackbar', result.response.data.data.message);
            }
        } catch (e) {
            console.error(e);
            eventEmitter.emit('showSnackbar', 'Failed to delete SLA configuration.');
        }
        setTableLoading(false);
    };

    const handleSaveSLA = async (row: TableRow) => {
        setTableLoading(true);

        // Find the selected project from projectList
        const selectedProject = projectList.find(
            (proj: any) => proj.Project_Name === row.PepwiseProject
        ) as Project | undefined;

        if (!selectedProject) {
            eventEmitter.emit('showSnackbar', 'Please select a valid project.');
            setTableLoading(false);
            return false;
        }

        // Fetch latest project details from backend
        const projectId = selectedProject.Project_Id;
        const programId = programID;
        const result = await getProjectDetailsForSLAData(projectId, programId);

        // Defensive: check if result is valid
        const projectData = result?.data?.programData?.[0];
        if (!projectData) {
            eventEmitter.emit('showSnackbar', 'Failed to fetch project details.');
            setTableLoading(false);
            return false;
        }
        if (row.SLAType === 'Weekly' && (!row.Days || row.Days.trim() === '')) {
            eventEmitter.emit('showSnackbar', 'Please select at least one day for Weekly SLA.');
            setTableLoading(false);
            return false;
        }

        // Prepare the payload using backend data
        const data = {
            id: projectData.Project_Id,
            projectOwner: projectData.Project_Owner,
            projectName: projectData.Project_Name,
            projectDescription: projectData.Project_Description,
            mulSector: projectData.Sectors,
            friendlyName: projectData.Friendly_Name,
            isSlaConfigured: true,
            slaType: row.SLAType === 'Weekly' ? 1 : 0,
            slaVrtn: getSlaVrtnValue(row.SLACriteria),
            slaAgg: getSlaAggValue(row.SLAAggregation),
            slaData: row.SLAType === 'Weekly'
                ? selectedRows
                    .filter(
                        r =>
                            r.PepwiseProject === row.PepwiseProject &&
                            r.SLAType === 'Weekly'
                    )
                    .map(r => {
                        const isEdited = r.id === row.id;
                        const source = isEdited ? row : r;
                        return {
                            id: r.id,
                            days: source.Days,
                            slaTime: source.SLATime && typeof source.SLATime === 'string'
                                ? Number(sanitizeSlaTime(source.SLATime))
                                : (() => {
                                    let slaTimeValue = 0;
                                    if (typeof source.SLATime === 'number') {
                                        slaTimeValue = source.SLATime;
                                    }
                                    return slaTimeValue;
                                })(),
                            slaDate: source.SLADate === '' ? 0 : Number(source.SLADate),
                            slaVrtn: getSlaVrtnValue(source.SLACriteria),
                            slaAgg: getSlaAggValue(source.SLAAggregation),
                        };
                    })
                : [],
            slaDate: row.SLADate === '' ? 0 : Number(row.SLADate),
            slaTime: typeof row.SLATime === 'string'
                ? Number(sanitizeSlaTime(row.SLATime))
                : (() => {
                    let slaTimeValue = 0;
                    if (typeof row.SLATime === 'number') {
                        slaTimeValue = row.SLATime;
                    }
                    return slaTimeValue;
                })(),
            isSlaChanged: true
        };

        // Optionally, convert mulSector to sector IDs if needed
        let sectorIds: string[] = [];
        for (const sector of sectorList) {
            if (projectData.Sectors.includes(sector.Sector_Name)) {
                sectorIds.push(sector.Sector_Id);
            }
        }
        data['mulSector'] = sectorIds;

        // Call the API
        try {
            const result = await editDataPipelineProject(data);
            if (result.data?.message) {
                await getProjectsByProgramData({});
                setTableLoading(false);
                return true;
            } else if (result.response?.status === 400) {
                eventEmitter.emit('showSnackbar', result.response.data.data.message);
            }
        } catch (e) {
            console.error(e);
            eventEmitter.emit('showSnackbar', 'Failed to save SLA.');
        }
        setTableLoading(false);
        return false;
    };

    const handleDeleteNewRow = () => {
        setSelectedRows(selectedRows.filter((row) => !row.isNew))
    };

    // Helper function to update new row in selectedRows
    function updateNewRowInSelectedRows(rows: TableRow[], updated: Partial<TableRow>) {
        return rows.map((row) =>
            row.isNew ? { ...row, ...updated } : row
        );
    }

    const handleChangeSLA = (field: string, value: any) => {
        setNewAllocation((prev) => {
            const updated = { ...prev, [field]: value };
            // Also update the new row in selectedRows
            setSelectedRows((rows) =>
                updateNewRowInSelectedRows(rows, updated)
            );
            return updated;
        });
    };

    const handleAllocationChange = (id: number, field: string, value: any) => {
        let newValue = value;
        if (field === 'SLATime') {
            if (value && typeof value === 'object' && typeof value.getHours === 'function') {
                // JS Date
                const hours = value.getHours().toString().padStart(2, '0');
                const minutes = value.getMinutes().toString().padStart(2, '0');
                newValue = `${hours}:${minutes}`;
            } else if (value && typeof value === 'object' && typeof value.hour === 'function') {
                // Dayjs
                const hours = value.hour().toString().padStart(2, '0');
                const minutes = value.minute().toString().padStart(2, '0');
                newValue = `${hours}:${minutes}`;
            } else if (typeof value === 'string') {
                newValue = value;
            } else {
                newValue = '';
            }
        }
        setCurrentEditRow((prev) => ({
            ...prev,
            [field]: newValue,
        }));

        setSelectedRows((prevRows) =>
        prevRows.map((row) => {
            if (row.id === id && row.isEditing) {
                if (field === 'SLAType') {
                    if (newValue === 'Daily') {
                        return { ...row, SLAType: newValue, Days: 'All Days' };
                    } else {
                        return { ...row, SLAType: newValue, Days: (row.Days === 'All Days' || !row.Days) ? '' : row.Days };
                    }
                }
                if (field === 'Days') {
                    return { ...row, Days: newValue };
                }
                return { ...row, [field]: newValue };
            }
            return row;
        })
    );
    };

    const handleSaveEdit = (rowId: number, updatedFields: { [key: string]: any }) => {
        setSelectedRows((prevRows) =>
            prevRows.map((row) => {
                if (row.id === rowId) {
                    if (updatedFields && Object.keys(updatedFields).length > 0) {
                        let SLATime = currentEditRow.SLATime;
                        // Convert Date or Dayjs-like object to "HH:mm" string
                        if (SLATime instanceof Date) {
                            const hours = SLATime.getHours().toString().padStart(2, '0');
                            const minutes = SLATime.getMinutes().toString().padStart(2, '0');
                            SLATime = `${hours}:${minutes}`;
                        } else if (
                            SLATime && typeof SLATime === 'object' &&
                            typeof (SLATime as any).$H === 'number' && typeof (SLATime as any).$m === 'number'
                        ) {
                            // Dayjs-like object
                            const hours = (SLATime as any).$H.toString().padStart(2, '0');
                            const minutes = (SLATime as any).$m.toString().padStart(2, '0');
                            SLATime = `${hours}:${minutes}`;
                        }
                        // If already a string, leave as is
                         const newSLADate = currentEditRow.SLADate;
                        return { ...row, ...currentEditRow, SLATime, slaDateValue: newSLADate, isEditing: false };
                    } else if (row.originalData) {
                        // Revert to original data and remove originalData property
                        const { originalData } = row;
                        return { ...originalData, isEditing: false };
                    }
                }
                return row;
            })
        );
    };

    const handleEditRow = (row: TableRow) => {
        setCurrentEditRow({
        ...row,
        SLADate: getSlaDateValue(row.SLADate)
        });
        setSelectedRows((prevRows) =>
            prevRows.map((r) =>
                r.id === row.id
                    ? { ...r, isEditing: true, originalData: structuredClone(r) }
                    : { ...r, isEditing: false }
            ));
    };

    function getWeeklyUsedDaysMap(selectedRows: TableRow[]) {
        const map: Record<string, string[]> = {};
        for (const row of selectedRows) {
            if (row.SLAType === 'Weekly' && row.PepwiseProject && row.Days) {
                const daysArr = row.Days.split(',').map(d => d.trim());
                if (!map[row.PepwiseProject]) map[row.PepwiseProject] = [];
                map[row.PepwiseProject].push(...daysArr);
            }
        };
        for (const proj of Object.keys(map)) {
            map[proj] = Array.from(new Set(map[proj]));
        };
        return map;
    }

    function hasWeeklySLA(projectName: string) {
        return selectedRows.some(
            row => row.PepwiseProject === projectName && row.SLAType === 'Weekly'
        );
    }

    function renderViewPepwiseProject(row: any) {
        return <RowTypography>{row.PepwiseProject}</RowTypography>;
    }

    function renderViewSLAType(row: any) {
        return <RowTypography>{row.SLAType}</RowTypography>;
    }

    function renderViewDays(row: any) {
        let days = row.Days;

        if (row.SLAType === "Daily") {
            days = "All Days";
        }

        return <RowTypography>{days}</RowTypography>;
    }

    function renderViewSLADate(row: any) {
        const displayDate =
            row.slaDateValue !== undefined &&
            row.slaDateValue !== null &&
            row.slaDateValue !== ""
                ? row.slaDateValue
                : "0";

        return <RowTypography>{displayDate}</RowTypography>;
    }

    function renderViewSLATime(row: any) {
        let displayTime = "";

        if (typeof row.SLATime === "string") {
            displayTime = row.SLATime;
        } else if (row.SLATime instanceof Date) {
            displayTime = `${row.SLATime.getHours().toString().padStart(2, "0")}:${row.SLATime.getMinutes().toString().padStart(2, "0")}`;
        }  else if (row.SLATime?.$H !== undefined && row.SLATime.$m !== undefined) {
            // Dayjs object support
            displayTime = `${row.SLATime.$H.toString().padStart(2, '0')}:${row.SLATime.$m.toString().padStart(2, '0')}`;
        }

        return <RowTypography>{displayTime}</RowTypography>;
    }

    function renderViewSLACriteria(row: any) {
        return <RowTypography>{row.SLACriteria || ""}</RowTypography>;
    }

    function renderViewSLAAggregation(row: any) {
        return <RowTypography>{row.SLAAggregation || ""}</RowTypography>;
    }

    function renderViewActions(row: any) {
        return (
            <Stack direction="row" spacing={1}>
                <CustomEditIcon title="Edit" size={20} color={"#39ACE7"}
                    onClick={() => handleEditRow(row)}
                />
                <CustomDeleteIcon title="Delete" size={20} color={"#FE2626"}
                    onClick={() => handleDeleteExistingRow(row)}
                />
            </Stack>
        );
    }

    function renderJobLevelViewActions(row: any) {
        return (
            <Stack direction="row" spacing={1}>
                <CustomEditIcon title="Edit" size={24} color={"#39ACE7"}
                    onClick={() => handleOpenJobLevelSLAConfig(row)}
                />
                <CustomDeleteIcon title="Delete" size={24} color={"#FE2626"}
                    onClick={() => handleDeleteExistingJobSLARow(row)}
                />
            </Stack>
        );
    }

    const findJobLevelSLAEntry = (row: any) => {
        try {
            if (jobLevelSLAData && jobLevelSLAData.slaList) {
                for (const projId of Object.keys(jobLevelSLAData.slaList)) {
                    const list = jobLevelSLAData.slaList[projId];
                    if (!Array.isArray(list)) continue;

                    const matched = list.find((it: any) =>
                        (it.jobName === row.jobName || it.jobId === row.jobName) &&
                        (it.projectName === row.PepwiseProject)
                    );
                    if (matched) return matched;
                }
            }
        } catch (e) {
            console.error('Error locating job-level SLA data', e);
        }
        return null;
    };

    const getJobLevelSelectedProject = (row: any, found: any) => {
        const baseProject = getSelectedProject(row) || {
            Project_Id: row.projectId || null,
            Project_Name: row.PepwiseProject,
            Project_Description: '',
            Project_Owner: '',
            Sectors: '',
            Friendly_Name: '',
            sectorIds: []
        } as any;

        return {
            ...baseProject,
            jobId: found?.jobId || row.jobId || baseProject.jobId || null,
            jobName: found?.jobName || row.jobName || baseProject.jobName || '',
            programId: programID,
            programName,
            platformName: found?.platformName || '',
            schedulerName: found?.schedulerName || ''
        };
    };

    const buildJobLevelInitialData = (found: any) => {
        const scheduleType = normalizeScheduleType(found.scheduleType || found.slaType || '');
        const deadline = found.deadline || {};
        const deadlineType = String(deadline.type || '').toUpperCase() === 'INTERVAL' ? 'Interval' : 'Clock';
        const slotsAvailable = deadline.slotting ? 'Yes' : 'No';
        const mergedSlots = mergeDailySlots(parseDeadlineSlots(deadline));
        const slaStartTimes = mergedSlots.map((slot: any, idx: number) => ({
            id: idx + 1,
            days: slot.days,
            startTime: slot.startTime,
            endTime: slot.endTime || '',
            duration: slot.duration,
            criticality: slot.isCritical
        }));

        return {
            isNew: false,
            scheduleType,
            deadlineType,
            rule: deadline.rule || '',
            slotsAvailable,
            duration: deadline.duration > 0 ? `${Number(deadline.duration)} Mins` : '',
            slaStartTimes: slaStartTimes.length ? slaStartTimes : [{ id: 1, days: [], startTime: '', endTime: '', duration: '', criticality: true }]
        };
    };

    const handleOpenJobLevelSLAConfig = (row: any, viewOnly: boolean = false) => {
        setIsSLAConfigViewOnly(viewOnly);

        const found = findJobLevelSLAEntry(row);
        const selectedProject = getJobLevelSelectedProject(row, found);

        if (!found) {
            handleOpenSLAConfigModal(row, viewOnly);
            return;
        }

        setConfigModalProject(selectedProject);
        const initialData = buildJobLevelInitialData(found);
        setConfigModalInitialData(initialData);
        setIsSLAConfigModalOpen(true);
        persistSlaModalState({ open: true, selectedProject, initialData, formData: initialData, viewOnly });
    };

    const handleDeleteExistingJobSLARow = (row: TableRow) => {
        const projectName = row.PepwiseProject;
        let projectId = row.projectId;
        let jobId = row.jobId;
        let platformName = row.platformName;
        let schedulerName = row.schedulerName;
        let jobName = row.jobName;

        if ((!projectId || !jobId || !platformName || !schedulerName) && jobLevelSLAData?.filterOptions) {
            const matchedProject = jobLevelSLAData.filterOptions.find((p: any) => p.projectName === projectName);
            if (matchedProject) {
                projectId = projectId || matchedProject.projectId;
                const matchedJob = matchedProject.jobs?.find(
                    (j: any) => j.jobId === jobId || j.jobName === jobName || j.jobName === row.jobName || j.jobId === row.jobName
                );
                if (matchedJob) {
                    jobId = jobId || matchedJob.jobId;
                    jobName = jobName || matchedJob.jobName;
                    platformName = platformName || matchedJob.platformName;
                    schedulerName = schedulerName || matchedJob.schedulerName;
                }
            }
        }

        const enrichedRow = {
            ...row,
            programId: row.programId || programID,
            programName: row.programName || programName,
            projectName,
            projectId,
            jobId,
            jobName,
            platformName,
            schedulerName
        };

        setOpenModal({
            ...openModal,
            isOpen: true,
            type: 'JobSLA',
            action: 'DELETE',
            data: enrichedRow
        });
    };

    const handleModalClose = (closeType: any) => {
        setOpenModal({
            ...openModal,
            isOpen: false,
            closeType: closeType
        });
    };

    const getToastData = (data: any) => {
        if (data?.data?.message) {
            eventEmitter.emit('showSnackbar', data.data.message);
        }
    };

    const getUsedJobsByProject = (rows: TableRow[], currentRowId?: number) => {
        const usedJobsByProject: Record<string, Set<string>> = {};

        rows.forEach((row) => {
            if (!row || row.id === currentRowId) {
                return;
            }

            const projectName = row.PepwiseProject;
            const jobName = row.jobName;

            if (projectName && jobName) {
                if (!usedJobsByProject[projectName]) {
                    usedJobsByProject[projectName] = new Set<string>();
                }
                usedJobsByProject[projectName].add(jobName);
            }
        });

        return usedJobsByProject;
    };

    const getAvailableProjectOptionsForRow = (row: any) => {
        const usedJobsByProject = getUsedJobsByProject(jobRows, row.id);

        return jobLevelProjects
            .filter((project: any) => {
                const projectJobs = Array.isArray(project.jobs) ? project.jobs : [];
                const availableJobs = projectJobs.filter((job: any) => {
                    const jobName = job.jobName || job.name || '';
                    if (!jobName) {
                        return false;
                    }
                    const usedJobs = usedJobsByProject[project.projectName] || new Set<string>();
                    return !usedJobs.has(jobName);
                });

                return availableJobs.length > 0 || project.projectName === row.PepwiseProject;
            })
            .map((project: any) => ({
                value: project.projectId,
                label: project.projectName
            }));
    };

    const getAvailableJobOptionsForRow = (row: any) => {
        const projectName = row.PepwiseProject;
        if (!projectName) {
            return [];
        }

        const selectedProject = jobLevelProjects.find((project: any) => project.projectName === projectName);
        const projectJobs = Array.isArray(selectedProject?.jobs) ? selectedProject.jobs : [];
        const usedJobsByProject = getUsedJobsByProject(jobRows, row.id);
        const usedJobs = usedJobsByProject[projectName] || new Set<string>();

        return projectJobs
            .filter((job: any) => {
                const jobName = job.jobName || job.name || '';
                if (!jobName) {
                    return false;
                }
                return !usedJobs.has(jobName) || jobName === row.jobName;
            })
            .map((job: any) => ({
                value: job.jobId,
                label: job.jobName || job.name || ''
            }));
    };

    const canShowAddJobLevelRowButton = isAdminUser && getAvailableProjectOptionsForRow({ id: -1, PepwiseProject: '' }).length > 0;

    const passInfoToParent = async () => {
        await getJobLevelSLAConfig();
    };

    function renderNewJobLevelProject(row: any) {
        const projectOptions = getAvailableProjectOptionsForRow(row);
        const selectedOption = projectOptions.find((opt: any) => opt.label === row.PepwiseProject) || null;

        return (
            <FormControl fullWidth>
                <CustomAutocomplete
                    fullWidth
                    size="small"
                    options={projectOptions}
                    value={selectedOption}
                    onChange={(event: any, value: any) => {
                        const projectName = value?.label || '';
                        handleJobLevelChange(row.id, 'PepwiseProject', projectName);
                    }}
                    textFieldLabel="Select Project"
                    popupIcon={<CustomArrowIcon color={'#009BD8'} />}
                    sx={{ width: '100%', ...getInputCustomCommonSx(colors,'#009BD8', '#009BD8') }}
                    componentsProps={getAutocompleteCommonComponentsProps(colors)}
                    customTextFieldProps={{
                        ...commonCustomTextFieldProps,
                        sx: {
                            ...commonCustomTextFieldProps.sx,
                            '& .MuiOutlinedInput-root': {
                                ...commonCustomTextFieldProps.sx?.['& .MuiOutlinedInput-root'],
                                '& fieldset': { borderColor: '#009BD8 !important' },
                                '&:hover fieldset': { borderColor: '#009BD8 !important' },
                                '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' }
                            }
                        }
                    }}
                />
            </FormControl>
        );
    }

    function renderNewJobLevelName(row: any) {
        const jobOptions = getAvailableJobOptionsForRow(row);
        const selectedJobOption = jobOptions.find((opt: any) => opt.label === row.jobName) || null;

        return (
            <FormControl fullWidth>
                <CustomAutocomplete
                    fullWidth
                    size="small"
                    options={jobOptions}
                    value={selectedJobOption}
                    onChange={(event: any, value: any) => {
                        const jobName = value?.label || '';
                        handleJobLevelChange(row.id, 'jobName', jobName);
                    }}
                    textFieldLabel="Select Job"
                    popupIcon={<CustomArrowIcon color={row.isNew ? '#009BD8' : colors.input.text} />}
                    sx={{ width: '100%', ...getInputCustomCommonSx(colors, '#009BD8', '#009BD8') }}
                    componentsProps={getAutocompleteCommonComponentsProps(colors)}
                    customTextFieldProps={{
                        ...commonCustomTextFieldProps,
                        sx: {
                            ...commonCustomTextFieldProps.sx,
                            '& .MuiOutlinedInput-root': {
                                ...commonCustomTextFieldProps.sx?.['& .MuiOutlinedInput-root'],
                                '& fieldset': { borderColor: '#009BD8 !important' },
                                '&:hover fieldset': { borderColor: '#009BD8 !important' },
                                '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' }
                            }
                        }
                    }}
                />
            </FormControl>
        );
    }

    function renderNewJobLevelActions(row: any) {
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', justifyContent: 'flex-start' }}>
                {renderConfigureAction(row)}
                <Box sx={{ display: 'flex', cursor: 'pointer', flexShrink: 0, alignItems: 'center', justifyContent: 'center', minWidth: '32px' }}>
                    <CustomDeleteIcon title="Delete Row" size={24} color={"#FE2626"}
                        onClick={() => handleDeleteRow(row.id)}
                    />
                </Box>
            </Stack>
        );
    }

    function renderConfigureAction(row: any) {
        const isDisabled = !row.PepwiseProject || !row.jobName;
        return (
            <Button
                variant="pepwiseSecondary"
                sx={{ textTransform: 'none', flexShrink: 0, whiteSpace: 'nowrap', fontSize: '12px' }}
                onClick={() => handleOpenSLAConfigModal(row)}
                disabled={isDisabled}
                title={isDisabled ? "Please select both project and job" : "Configure SLA"}
            >
                Configure SLA
            </Button>
        );
    }

    const jobLevelDefaultColumnNamesSLA = [
        'Projects',
        'Jobs',
        'SLA Type',
        'Schedule Type',
        'Deadline Type',
        'Rule',
        'Actions'
    ];

    const getJobLevelColumnsSLA = () => {
        const columns: any[] = [
            {
                key: 'PepwiseProject',
                name: 'Projects',
                formatter: ({ row }: any) =>
                    row.isNew ? renderNewJobLevelProject(row) : renderJobLevelProjectName(row),
                editable: false,
                width: '1fr',
                minWidth: 180,
                resizable: true
            },
            {
                key: 'jobName',
                name: 'Jobs',
                formatter: ({ row }: any) =>
                    row.isNew ? renderNewJobLevelName(row) : renderJobLevelJobs(row),
                editable: false,
                width: '1fr',
                minWidth: 180,
                resizable: true
            },
            {
                key: 'scheduleType',
                name: 'Schedule Type',
                formatter: ({ row }: any) => renderScheduleType(row),
                editable: false,
                width: 150,
                minWidth: 90,
                maxWidth: 160
            },
            {
                key: 'deadlineType',
                name: 'Deadline Type',
                formatter: ({ row }: any) => renderDeadlineType(row),
                editable: false,
                width: 150,
                minWidth: 90,
                maxWidth: 160
            },
            {
                key: 'rule',
                name: 'Rule',
                formatter: ({ row }: any) => renderRule(row),
                editable: false,
                width: 180,
                minWidth: 120,
                maxWidth: 200
            }
        ];

        if (isAdminUser) {
            columns.push({
                key: 'actions',
                name: 'Actions',
                formatter: ({ row }: any) => (row.isNew ? renderNewJobLevelActions(row) : renderJobLevelViewActions(row)),
                editable: false,
                width: 180,
                minWidth: 180,
                maxWidth: 180,
            });
        }

        return columns;
    };

    function renderJobLevelProjectName(row: any) {
        return <RowTypography>{row.PepwiseProject || '-'}</RowTypography>;
    }

    function renderJobLevelJobs(row: any) {
        if (!row.jobName) {
            return <RowTypography>-</RowTypography>;
        }

        return (
            <Link
                component="button"
                underline="none"
                onClick={() => handleOpenJobLevelSLAConfig(row, true)}
                sx={{
                    display: 'block',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#000',
                    textAlign: 'left',
                    cursor: 'pointer'
                }}
                title={row.jobName}
            >
                {row.jobName}
            </Link>
        );
    }

    function renderScheduleType(row: any) {
        return (
            <RowTypography sx={{ color: row.scheduleType ? 'inherit' : '#71758F' }}>
                {row.scheduleType || 'Configure'}
            </RowTypography>
        );
    }

    function renderDeadlineType(row: any) {
        return (
            <RowTypography sx={{ color: row.deadlineType ? 'inherit' : '#71758F' }}>
                {row.deadlineType || 'Configure'}
            </RowTypography>
        );
    }

    function renderRule(row: any) {
        return (
            <RowTypography sx={{ color: row.rule ? 'inherit' : '#71758F' }}>
                {row.rule || 'Configure'}
            </RowTypography>
        );
    }

    function renderEditActions(row: any) {
        return (
            <Stack direction="row" spacing={1}>
                {/* Cancel */}
                <CloseIcon
                    title="Cancel"
                    style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                    onClick={() => {
                        setEditValidationErrors({ project: false });
                        setIsDuplicate(false);
                        handleSaveEdit(row.id, {}); // exit edit mode
                    }}
                />

                {/* Save */}
                <SaveIcon
                    title="Save"
                    style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                    onClick={async () => {
                        // Duplicate validation
                        const hasDuplicate = selectedRows.some(
                            (r) =>
                                r.id !== row.id &&
                                r.PepwiseProject === currentEditRow.PepwiseProject &&
                                r.SLAType === currentEditRow.SLAType &&
                                r.Days === currentEditRow.Days
                        );
                        setIsDuplicate(hasDuplicate);

                        // Required field validation
                        const hasError = !currentEditRow.PepwiseProject;
                        setEditValidationErrors({ project: hasError });

                        if (!hasDuplicate && !hasError) {
                            const success = await handleSaveSLA(currentEditRow);
                            if (success) {
                                handleSaveEdit(row.id, currentEditRow);
                            }
                        }
                    }}
                />
            </Stack>
        );
    }

    function renderEditSLACriteria(row: any) {
        return (
            <FormControl fullWidth>
                <Select
                    value={currentEditRow.SLACriteria || ""}
                    onChange={e => handleAllocationChange(row.id, 'SLACriteria', e.target.value)}
                    displayEmpty
                    size="small"
                    IconComponent={ExpandMoreIcon}
                    sx={{
                        minWidth: '12rem',
                        color: 'textLight.main',
                        '& .MuiSelect-select': {
                            color: 'textLight.main'
                        },
                        '& .MuiSelect-icon': {
                            color: '#009BD8 !important',
                            fill: '#009BD8 !important'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '& .MuiMenu-paper': {
                            backgroundColor: '#003771 !important',
                        }
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                backgroundColor: '#003771',
                                color: 'textLight.main'
                            }
                        }
                    }}
                >
                    <MenuItem value="" disabled sx={{ color: '#fff' }}>Select SLA Criteria</MenuItem>
                    <MenuItem value="All Jobs Completed in Last 24 Hrs">All Jobs Completed in Last 24 Hrs</MenuItem>
                    <MenuItem value="All Jobs Completed on Same Day">All Jobs Completed on Same Day</MenuItem>
                </Select>
            </FormControl>
        );
    }

    function renderEditSLAAggregation(row: any) {
        return (
            <FormControl fullWidth>
                <Select
                    value={currentEditRow.SLAAggregation || ""}
                    onChange={e => handleAllocationChange(row.id, 'SLAAggregation', e.target.value)}
                    displayEmpty
                    size="small"
                    IconComponent={ExpandMoreIcon}
                    sx={{
                        minWidth: '12rem',
                        color: 'textLight.main',
                        '& .MuiSelect-select': {
                            color: 'textLight.main'
                        },
                        '& .MuiSelect-icon': {
                            color: '#009BD8 !important',
                            fill: '#009BD8 !important'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                        },
                        '& .MuiMenu-paper': {
                            backgroundColor: '#003771 !important',
                        }
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                backgroundColor: '#003771',
                                color: 'textLight.main'
                            }
                        }
                    }}
                >
                    <MenuItem value="" disabled sx={{ color: '#fff' }}>Select SLA Aggregation</MenuItem>
                    <MenuItem value="PROJECT_SLA_AGG">PROJECT_SLA_AGG</MenuItem>
                    <MenuItem value="JOB_SLA_AGG">JOB_SLA_AGG</MenuItem>
                </Select>
            </FormControl>
        );
    }

    const formatTimeOnBlur = (
        e: any,
        onFormatted: (formatted: string) => void
    ) => {
        let input = e.target.value;
        let digits = input.replace(/\D/g, '');
        // Get current value for fallback
        let current = /^(\d{2}):(\d{2})$/.exec(input);
        let hours = current ? current[1] : '00';
        let minutes = current ? current[2] : '00';
        if (digits.length === 2) {
            // If cursor at start, update hours; if at end, update minutes
            if (e.target.selectionStart !== null && e.target.selectionStart <= 2) {
                hours = digits;
            } else {
                minutes = digits;
            }
        } else if (digits.length === 4) {
            hours = digits.slice(0, 2);
            minutes = digits.slice(2, 4);
        } else if (digits.length === 1) {
            // Single digit, update minutes
            minutes = '0' + digits;
        } else if (digits.length === 3) {
            // 3 digits: first is hour, next two are minutes
            hours = '0' + digits[0];
            minutes = digits.slice(1, 3);
        }
        // Validate
        let h = Number.parseInt(hours, 10);
        let m = Number.parseInt(minutes, 10);
        if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) {
            hours = '00';
            minutes = '00';
        } else {
            hours = h.toString().padStart(2, '0');
            minutes = m.toString().padStart(2, '0');
        }

        onFormatted(`${hours}:${minutes}`);
    };
    const slaTimeInputSx = {
        background: 'transparent',
        color: 'textLight.main',
        fontSize: '14px',
        fontWeight: 500,
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none'
        },
        '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'none'
        },
        '& .MuiSvgIcon-root': {
            color: '#009BD8 !important',
            fill: '#009BD8 !important'
        },
        '& input::placeholder': {
            color: 'textLight.main',
            opacity: 1
        }
    };

    const slaTimeFieldSx = {
        background: 'primary.darker',
        borderRadius: '8px',
        input: { color: 'textLight.main' },
        fieldset: { border: 'none' },
        '& .MuiOutlinedInput-root': {
            color: 'textLight.main',
            borderRadius: '5px',
            border: 'none',
            background: 'transparent'
        },
        '& .MuiInputLabel-root, & .MuiSvgIcon-root': {
            color: '#009BD8 !important',
            fill: '#009BD8 !important'
        },
        '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none'
        }
    };

    const getSlaTimeInputProps = (params: any) => ({
        ...params.InputProps,
        sx: slaTimeInputSx,
        inputProps: {
            ...params.inputProps,
            placeholder: 'Select SLA Time'
        }
    });

    function renderEditSLATime(row: any) {
        const editRow = row.id === currentEditRow.id ? currentEditRow : row;
        return (
            <FormControl fullWidth>
                <CustomTimePicker
                    onChange={(dateOrStr: any) => {
                        if (typeof dateOrStr === 'string') {
                            handleAllocationChange(row.id, 'SLATime', dateOrStr);
                        } else if (dateOrStr && typeof dateOrStr === 'object' && typeof dateOrStr.hour === 'function' && typeof dateOrStr.minute === 'function') {
                            const hours = dateOrStr.hour().toString().padStart(2, '0');
                            const minutes = dateOrStr.minute().toString().padStart(2, '0');
                            handleAllocationChange(row.id, 'SLATime', `${hours}:${minutes}`);
                        } else if (dateOrStr instanceof Date && !Number.isNaN(dateOrStr.getTime())) {
                            const hours = dateOrStr.getHours().toString().padStart(2, '0');
                            const minutes = dateOrStr.getMinutes().toString().padStart(2, '0');
                            handleAllocationChange(row.id, 'SLATime', `${hours}:${minutes}`);
                        } else {
                            handleAllocationChange(row.id, 'SLATime', '');
                        }
                    }}
                    ampm={false}
                    value={timeStringToDate(editRow.SLATime) || null}
                    renderInput={(params) => {
                        const { label, ...restParams } = params;
                        return (
                        <TextField
                        {...restParams}
                        value={editRow.SLATime || ''}
                        onChange={e => {
                            handleAllocationChange(row.id, 'SLATime', e.target.value);
                        }}
                        onBlur={e => {
                            formatTimeOnBlur(e, (formatted) =>
                                handleAllocationChange(row.id, 'SLATime', formatted)
                            );
                        }}
                        InputProps={getSlaTimeInputProps(params)}
                        sx={slaTimeFieldSx}
                    />
                );
                }}
            />
            </FormControl>
        );
    }

    function renderEditSLADate(row: any) {
        return (
            <FormControl fullWidth>
                <CustomTextField
                    id="slaDate"
                    name="slaDate"
                    autoComplete="off"
                    value={
                        currentEditRow.SLADate !== undefined &&
                        currentEditRow.SLADate !== null &&
                        currentEditRow.SLADate !== ""
                            ? String(currentEditRow.SLADate)
                            : "0"
                    }
                    type="number"
                    className="sla-date-edit"
                    inputProps={{
                        max: 1,
                        min: 0,
                        step: 1,
                        maxLength: 1,
                        style: {
                            background: "transparent",
                            color: `${colors.textLight.main} !important`,
                            border: "none",
                        },
                    }}
                    onChange={(e) => {
                        const value = e.target.value;

                        if (value === "" || value === "0" || value === "1") {
                            handleAllocationChange(row.id, "SLADate", value);
                        } else {
                            e.target.value = "1";
                            handleAllocationChange(row.id, "SLADate", "1");
                        }
                    }}
                    InputLabelProps={{
                        sx: {
                            "&&": { color: `${colors.textLight.main} !important` },
                            "&.Mui-focused": { color: `${colors.textLight.main} !important` },
                            "&.MuiInputLabel-shrink": {
                                color: `${colors.textLight.main} !important`,
                            },
                            "&.Mui-error": {
                                color: `${colors.textLight.main} !important`,
                            },
                        },
                    }}
                    InputProps={{
                        sx: {
                            "&&": { color: `${colors.textLight.main} !important` },
                            "& input": {
                                color: `${colors.textLight.main} !important`,
                                WebkitTextFillColor: `${colors.textLight.main} !important`,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.textLight.main} !important`,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.textLight.main} !important`,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.textLight.main} !important`,
                            },
                        },
                    }}
                    sx={{
                        background: "transparent",
                        "& .MuiOutlinedInput-root": {
                            background: "transparent",
                            color: "none",
                            border: "none",
                            boxShadow: "none",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            color: "none",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            color: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            color: "none",
                        },
                    }}
                />
            </FormControl>
        );
    }

    function renderEditDays(row: any) {
        const isDaily = currentEditRow.SLAType === 'Daily';
        const isDisabled = isDaily;
        const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const dayDropdownOptions = isDaily
            ? [{ label: 'All Days', value: 'All Days' }]
            : allDays.map(day => ({ label: day, value: day }));
        // Get selected days for this row
        let selectedDays: string[] = [];
        if (Array.isArray(currentEditRow.Days)) {
            selectedDays = currentEditRow.Days;
        } else if (typeof currentEditRow.Days === 'string' && currentEditRow.Days.length > 0) {
            selectedDays = currentEditRow.Days.split(',').map((d: string) => d.trim());
        }
        const selected = selectedDays;

        // Find all days already used by other weekly rows for this project (excluding this row)
        const usedDays = new Set<string>(
            selectedRows
            .filter(
                r =>
                    r.PepwiseProject === currentEditRow.PepwiseProject &&
                    r.SLAType === 'Weekly' &&
                    r.id !== row.id // exclude current editing row
            )
            .flatMap(r =>
                r.Days === 'All Days'
                    ? allDays
                    : r.Days.split(',').map((d: string) => d.trim())
            )
        );
        return (
            <FormControl fullWidth>
                <Select
                    labelId={`select-days-label-${row.id}`}
                    multiple
                    size="small"
                    value={selected}
                    disabled={isDisabled}
                    IconComponent={isDisabled ? undefined : ExpandMoreIcon}
                    onChange={(e) => {
                        const newDays = e.target.value as string[];
                        handleAllocationChange(
                            row.id,
                            'Days',
                            newDays.includes('Mon, Tue, Wed, Thu, Fri, Sat, Sun')
                                ? 'Mon, Tue, Wed, Thu, Fri, Sat, Sun'
                                : newDays.join(', ')
                        );
                    }}
                    renderValue={(selected) =>
                        (selected).length === 0
                            ? <span style={{
                                color: `${colors.textLight.main} !important`,
                                fontFamily: 'inherit',
                                fontWeight: 400,
                                fontSize: '1rem'
                            }}>Select Days</span>
                            : (selected).join(', ')
                    }
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 280,
                                overflowX: 'hidden',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                backgroundColor: '#ffffff !important',
                                color: '#000000 !important',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                '& .MuiMenuItem-root': {
                                    color: '#000000 !important',
                                    backgroundColor: '#ffffff !important',
                                    fontSize: '14px',
                                    '&:hover': {
                                        backgroundColor: '#EFF6FF !important',
                                        color: '#000000 !important'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#EFF6FF !important',
                                        color: '#000000 !important',
                                        fontWeight: 600
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#dbeafe !important',
                                        color: '#000000 !important'
                                    },
                                    '&.Mui-disabled': {
                                        color: '#94a3b8 !important',
                                        backgroundColor: '#ffffff !important'
                                    }
                                },
                                '& .MuiCheckbox-root': {
                                    color: '#009BD8 !important',
                                    padding: '4px',
                                    marginRight: '8px',
                                    '&.Mui-checked': {
                                        color: '#009BD8 !important'
                                    },
                                    '& svg': {
                                        fill: '#009BD8 !important'
                                    }
                                }
                            }
                        }
                    }}
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        '& .MuiSelect-select': {
                            color: `${colors.textLight.main} !important`,
                            WebkitTextFillColor: `${colors.textLight.main} !important`,
                            opacity: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden !important',
                            textOverflow: 'ellipsis',
                        },
                        '& .MuiSelect-icon': {
                            color: '#009BD8 !important',
                            fill: '#009BD8 !important'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none !important',
                        },
                        boxShadow: 'none',
                    }}
                >
                    {dayDropdownOptions.map((day) => (
                        <MenuItem
                            key={day.value}
                            value={day.value}
                            disabled={
                                isDisabled ||
                                (
                                    // Disable if day is used by other rows and not already selected in this row
                                    usedDays.has(day.value) && !selected.includes(day.value)
                                )
                            }
                        >
                            <Checkbox
                                checked={selected.includes(day.value)}
                                disabled={
                                    isDisabled ||
                                    (usedDays.has(day.value) && !selected.includes(day.value))
                                }
                                sx={{
                                    color: '#009BD8 !important',
                                    p: '4px',
                                    mr: 1,
                                    '&.Mui-checked': {
                                        color: '#009BD8 !important'
                                    },
                                    '& svg': {
                                        fill: '#009BD8 !important'
                                    }
                                }}
                            />
                            {day.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    function renderEditSLAType(row: any) {
        return (
            <FormControl fullWidth>
                <Select
                    value={currentEditRow.SLAType}
                    onChange={(e) => {
                        const value = e.target.value;
                        handleAllocationChange(row.id, 'SLAType', value);
                        if (value === 'Daily') {
                            // Patch as "All Days" instead of comma-separated days
                            handleAllocationChange(row.id, 'Days', 'All Days');
                        } else if (!currentEditRow.Days || currentEditRow.Days === 'All Days') {
                            handleAllocationChange(row.id, 'Days', '');
                        }
                    }}
                    size="small"
                    displayEmpty
                    IconComponent={ExpandMoreIcon}
                    sx={{
                        minWidth: '9.5rem',
                        background: 'transparent',
                        boxShadow: 'none',
                        '& .MuiSelect-select': {
                            color: `${colors.textLight.main} !important`,
                        },
                        '& .MuiSelect-icon': {
                            color: '#009BD8 !important',
                            fill: '#009BD8 !important'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                            color: '#009BD8 !important',
                            fill: '#009BD8 !important'
                        },
                    }}
                    disabled={currentEditRow.SLAType === 'Weekly'}
                >
                    <MenuItem value="" disabled>Select SLA Type</MenuItem>
                    <MenuItem value="Daily">Daily</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                </Select>
            </FormControl>
        );
    }

    function renderEditPepwiseProject(row: any, column: any) {
        return (
            <FormControl fullWidth error={!!editValidationErrors.project}>
                <AsyncAutoComplete
                    value={currentEditRow.PepwiseProject}
                    inputValue={currentEditRow.PepwiseProject}
                    options={projectOptions}
                    onChange={(event, value) => {
                        handleAllocationChange(row.id, column.key, value);
                    }}
                    style={{ width: '9.5rem' }}
                    mappingType={''}
                    columnName={''}
                    disabled
                    customTextFieldProps={{
                        InputLabelProps: {
                            sx: {
                            '&&': { color: `${colors.textLight.main} !important` },
                            '&.Mui-focused': { color: `${colors.textLight.main} !important` },
                            '&.MuiInputLabel-shrink': { color: `${colors.textLight.main} !important` },
                            },
                        },
                        InputProps: {
                            sx: {
                            '&&': { color: `${colors.textLight.main} !important` },
                            '& input': { color: `${colors.textLight.main} !important` },
                            },
                        },
                        sx: {
                            '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: `${colors.textLight.main} !important` },
                            '&:hover fieldset': { borderColor: `${colors.textLight.main} !important` },
                            '&.Mui-focused fieldset': { borderColor: `${colors.textLight.main} !important` },
                            },
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'none',
                            border: 'none'
                        },
                        '& .MuiAutocomplete-popupIndicator.Mui-disabled svg': {
                            color: 'none',
                        },
                        '& .MuiAutocomplete-popupIndicator.Mui-disabled svg path': {
                            fill: 'none',
                        }
                    }}
                />
            </FormControl>
        );
    }

    function renderNewActions() {
        const disableSave = !newAllocation?.SLADate || !newAllocation?.SLATime || !newAllocation?.SLACriteria || !newAllocation?.SLAAggregation ;
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <SaveIcon title="Save" className="rm-icons" 
                style={{
                    cursor: disableSave ? 'not-allowed' : 'pointer',
                    opacity: disableSave ? 0.4 : 1
                }}
                onClick={() => {
                    if (!disableSave) {
                        handleAddSLA();
                    }
                }} />
                <CustomDeleteIcon title="Delete Row" size={32} color={"#FE2626"}
                    onClick={handleDeleteNewRow}
                />
            </Stack>
        );
    }

    function renderNewSLAAggregation() {
        return (
            <FormControl fullWidth>
                <Select
                    value={newAllocation.SLAAggregation || ""}
                    onChange={e => handleChangeSLA('SLAAggregation', e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ minWidth: '12rem', fontSize: '14px', fontWeight: 500,
                        '& .MuiSelect-select': {
                            color: 'textLight.main'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                            },
                        }}
                >
                    <MenuItem value="" disabled sx={{ color: '#fff' }}>Select SLA Aggregation</MenuItem>
                    <MenuItem value="PROJECT_SLA_AGG">PROJECT_SLA_AGG</MenuItem>
                    <MenuItem value="JOB_SLA_AGG">JOB_SLA_AGG</MenuItem>
                </Select>
            </FormControl>
        );
    }

     function renderNewSLACriteria() {
        return (
            <FormControl fullWidth>
                <Select
                    value={newAllocation.SLACriteria || ""}
                    onChange={e => handleChangeSLA('SLACriteria', e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ minWidth: '12rem', fontSize: '14px', fontWeight: 500,
                        '& .MuiSelect-select': {
                            color: 'textLight.main'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                            },
                        }}
                >
                    <MenuItem value="" disabled sx={{ color: '#fff' }}>Select SLA Criteria</MenuItem>
                    <MenuItem value="All Jobs Completed in Last 24 Hrs">All Jobs Completed in Last 24 Hrs</MenuItem>
                    <MenuItem value="All Jobs Completed on Same Day">All Jobs Completed on Same Day</MenuItem>
                </Select>
            </FormControl>
        );
    }
    
    function renderNewSLATime() {
        return (
            <FormControl fullWidth>
                <CustomTimePicker
                    onChange={(dateOrStr: any) => {
                        if (typeof dateOrStr === 'string') {
                            // Allow typing, format only on blur
                            handleChangeSLA('SLATime', dateOrStr);
                        } else if (dateOrStr && typeof dateOrStr === 'object' && typeof dateOrStr.hour === 'function' && typeof dateOrStr.minute === 'function') {
                            // Dayjs object
                            const hours = dateOrStr.hour().toString().padStart(2, '0');
                            const minutes = dateOrStr.minute().toString().padStart(2, '0');
                            handleChangeSLA('SLATime', `${hours}:${minutes}`);
                        } else if (dateOrStr instanceof Date && !Number.isNaN(dateOrStr.getTime())) {
                            // JS Date
                            const hours = dateOrStr.getHours().toString().padStart(2, '0');
                            const minutes = dateOrStr.getMinutes().toString().padStart(2, '0');
                            handleChangeSLA('SLATime', `${hours}:${minutes}`);
                        } else {
                            handleChangeSLA('SLATime', '');
                        }
                    }}
                    ampm={false}
                    value={timeStringToDate(newAllocation.SLATime) || null}
                    renderInput={(params) => {
                        const { label, ...restParams } = params;
                        return (
                        <TextField
                        {...restParams}
                        value={newAllocation.SLATime || ''}
                        onChange={e => {
                        // Allow free typing
                        handleChangeSLA('SLATime', e.target.value);
                        }}
                        onBlur={e => {
                            formatTimeOnBlur(e, (formatted) =>
                                handleChangeSLA('SLATime', formatted)
                            );
                        }}
                    InputProps={getSlaTimeInputProps(params)}
                    sx={slaTimeFieldSx}
                />
            );
                }}
            />
            </FormControl>
        );
    }

    function renderNewSLADate() {
        return (
            <FormControl fullWidth>
                <CustomTextField
                    id="slaDate"
                    name="slaDate"
                    autoComplete="off"
                    value={newAllocation.SLADate || ''}
                    type="number"
                    className="sla-date-edit"
                    inputProps={{
                        max: 1,
                        min: 0,
                        step: 1,
                        maxLength: 1,
                        style: {
                            background: 'transparent',
                            color: `${colors.textLight.main} !important`,
                            border: 'none',

                        }
                    }}
                    onChange={(e) => {
                        let value = e.target.value;
                        if (value === '' || value === '0' || value === '1') {
                            handleChangeSLA('SLADate', value);
                        } else {
                            e.target.value = '1';
                            handleChangeSLA('SLADate', '1');
                        }
                    }}
    
                    InputLabelProps={{
                        sx: {
                        '&&': { color: `${colors.textLight.main} !important` },
                        '&.Mui-focused': { color: `${colors.textLight.main} !important` },
                        '&.MuiInputLabel-shrink': { color: `${colors.textLight.main} !important` },
                        '&.Mui-error': { color: `${colors.textLight.main} !important` },
                        },
                    }}

                    InputProps={{
                        sx: {
                        '&&': { color: `${colors.textLight.main} !important` },
                        '& input': {
                            color: `${colors.textLight.main} !important`,
                            WebkitTextFillColor: `${colors.textLight.main} !important`, // Chrome/Safari & disabled
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${colors.textLight.main} !important`,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${colors.textLight.main} !important`,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${colors.textLight.main} !important`,
                        },
                        },
                    }}

                    sx={{
                        background: 'transparent',
                        '& .MuiOutlinedInput-root': {
                            background: 'transparent',
                            color: 'none',
                            border: 'none',
                            boxShadow: 'none',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                            color: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                            color: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                            color: 'none',
                        },
                    }}
                />
            </FormControl>
        );
    }

    function renderNewDays() {
        const isDaily = newAllocation.SLAType === 'Daily';
        if (isDaily) {
            if (newAllocation.Days !== 'All Days') {
                setTimeout(() => handleChangeSLA('Days', 'All Days'), 0);
            }
            return (
                <FormControl fullWidth>
                    <Select
                        value={['All Days']}
                        multiple
                        size="small"
                        disabled
                        renderValue={() => 'All Days'}
                        sx={{
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden',
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            '& .MuiSelect-select': {
                                color: `${colors.textLight.main} !important`,
                                WebkitTextFillColor: `${colors.textLight.main} !important`,
                                opacity: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden !important',
                                textOverflow: 'ellipsis',
                            },

                            // Remove background/border for disabled state
                            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                border: 'none !important',
                            },
                            '&.Mui-disabled': {
                                background: 'transparent',
                            },
                        }}
                    >
                        <MenuItem value="All Days">
                            <Checkbox checked />
                            All Days
                        </MenuItem>
                    </Select>
                </FormControl>
            );
        }
        // Weekly logic: disable already-used days for this project
        const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const dayDropdownOptions = [
            { label: 'All Days', value: 'All Days' },
            ...allDays.map(day => ({ label: day, value: day }))
        ];

        let selected: string[];
        if (typeof newAllocation.Days === 'string' && newAllocation.Days.trim().length > 0) {
            selected = newAllocation.Days === 'All Days'
                ? allDays
                : newAllocation.Days.split(',').map((d: string) => d.trim());
        } else {
            selected = [];
        }

        // Get weekly rows for this project (excluding the new row)
        const projectWeeklyRows = selectedRows.filter((row) =>
            row.PepwiseProject === newAllocation.PepwiseProject &&
            row.SLAType === "Weekly" &&
            !row.isNew
        );

        // Extract all used days
        const usedDaysArray = projectWeeklyRows.flatMap((row) =>
            row.Days === "All Days"
                ? allDays
                : row.Days.split(",").map((d: string) => d.trim())
        );
        const usedDays = new Set<string>(usedDaysArray.flat());

        const handleDaysChange = (e: SelectChangeEvent<string[]>) => {
            let value = e.target.value as string[];
            if (value.includes('All Days')) {
                value = allDays;
            }
            if (allDays.every(day => value.includes(day))) {
                value = ['All Days'];
            }
            handleChangeSLA(
                'Days',
                value.includes('All Days') ? 'All Days' : value.join(', ')
            );
        };

        return (
            <FormControl fullWidth>
                <Select
                    labelId={`select-days-label-new`}
                    multiple
                    size="small"
                    value={selected}
                    onChange={handleDaysChange}
                    displayEmpty
                    IconComponent={ExpandMoreIcon}
                    renderValue={selected => {
                        if ((selected).length === 0) {
                            return <span style={{
                                color: `${colors.textLight.main} !important`,
                                fontFamily: 'inherit',
                                fontWeight: 500,
                                fontSize: '14px'
                            }}>Select Days</span>;
                        }
                        if ((selected).length === allDays.length) {
                            return 'All Days';
                        }
                        return (selected).join(', ');
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 280,
                                overflowX: 'hidden',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                backgroundColor: '#ffffff !important',
                                color: '#000000 !important',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                '& .MuiMenuItem-root': {
                                    color: '#000000 !important',
                                    backgroundColor: '#ffffff !important',
                                    fontSize: '14px',
                                    '&:hover': {
                                        backgroundColor: '#EFF6FF !important',
                                        color: '#000000 !important'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#EFF6FF !important',
                                        color: '#000000 !important',
                                        fontWeight: 600
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#dbeafe !important',
                                        color: '#000000 !important'
                                    },
                                    '&.Mui-disabled': {
                                        color: '#94a3b8 !important',
                                        backgroundColor: '#ffffff !important'
                                    }
                                },
                                '& .MuiCheckbox-root': {
                                    color: '#009BD8 !important',
                                    padding: '4px',
                                    marginRight: '8px',
                                    '&.Mui-checked': {
                                        color: '#009BD8 !important'
                                    },
                                    '& svg': {
                                        fill: '#009BD8 !important'
                                    }
                                }
                            }
                        }
                    }}
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        '& .MuiSelect-select': {
                            color: `${colors.textLight.main} !important`,
                            WebkitTextFillColor: `${colors.textLight.main} !important`,
                            opacity: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden !important',
                            textOverflow: 'ellipsis',
                        },
                        '& .MuiSelect-icon': {
                            color: '#009BD8 !important',
                            fill: '#009BD8 !important'
                        },
                    }}
                >
                    {dayDropdownOptions.map(day => (
                        <MenuItem
                            key={day.value}
                            value={day.value}
                            disabled={
                                // Disable "All Days" always
                                day.value === 'All Days' ||
                                // Disable used days (unless already selected)
                                (day.value !== 'All Days' &&
                                    usedDays.has(day.value) &&
                                    !selected.includes(day.value))
                            }
                        >
                            <Checkbox
                                checked={selected.includes(day.value)}
                                disabled={
                                    day.value === 'All Days' ||
                                    (day.value !== 'All Days' &&
                                        usedDays.has(day.value) &&
                                        !selected.includes(day.value))
                                }
                                sx={{
                                    color: '#009BD8 !important',
                                    p: '4px',
                                    mr: 1,
                                    '&.Mui-checked': {
                                        color: '#009BD8 !important'
                                    },
                                    '& svg': {
                                        fill: '#009BD8 !important'
                                    }
                                }}
                            />
                            {day.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    function renderNewSLAType() {
        const selectedProject = newAllocation.PepwiseProject;
        const dailyDisabled = hasWeeklySLA(selectedProject);
        return (
            <FormControl fullWidth>
                <Select
                    value={newAllocation.SLAType}
                    onChange={(e) => {
                        const value = e.target.value;
                        handleChangeSLA('SLAType', value);
                        if (value === 'Daily') {
                            handleChangeSLA('Days', 'All Days');
                        } else {
                            handleChangeSLA('Days', '');
                        }
                    }}
                    size="small"
                    displayEmpty
                    IconComponent={() => null}
                    sx={{ minWidth: '4.5rem', fontSize: '14px', fontWeight: 500,
                        '& .MuiSelect-select': {
                        color: `${colors.textLight.main} !important`,
                        },

                        '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                        },
                        '& .MuiSvgIcon-root': {
                        color: `${colors.textLight.main} !important`,
                        },
                        }}
                >
                    <MenuItem value="" disabled sx={{ fontSize: '12px' }}>Select SLA Type</MenuItem>
                    <MenuItem value="Daily" disabled={!!dailyDisabled}>Daily</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                </Select>
            </FormControl>
        );
    }

    function renderNewPepwiseProject() {
        return (
            <FormControl fullWidth error={validationErrors.project}>
                <Select
                    value={newAllocation.PepwiseProject || ''}
                    onChange={(e) => {
                        handleChangeSLA('PepwiseProject', e.target.value);
                    }}
                    size="small"
                    displayEmpty
                    IconComponent={() => null}
                    sx={{
                        minWidth: '9.5rem',
                        fontSize: '14px',
                        fontWeight: 500,
                        '& .MuiSelect-select': {
                            color: `${colors.textLight.main} !important`,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                            color: `${colors.textLight.main} !important`,
                        },
                    }}
                >
                    <MenuItem value="" disabled sx={{ fontSize: '12px' }}>
                        Select Project
                    </MenuItem>
                    {filteredProjectOptions?.map((opt: any) => {
                        const label = typeof opt === 'string' ? opt : (opt.label || opt.value);
                        return (
                            <MenuItem key={label} value={label}>
                                {label}
                            </MenuItem>
                        );
                    })}
                </Select>
                {validationErrors.project && !isDuplicate && (
                    <FormHelperText>Project is required.</FormHelperText>
                )}
            </FormControl>
        );
    }

    const getColumnsSLA = () => {
        type ColumnRendererMap  = {
            PepwiseProject: (row?: any, column?: any) => JSX.Element;
            SLAType: (row?: any, column?: any) => JSX.Element;
            Days: (row?: any, column?: any) => JSX.Element;
            SLADate: (row?: any, column?: any) => JSX.Element;
            SLATime: (row?: any, column?: any) => JSX.Element;
            SLACriteria: (row?: any, column?: any) => JSX.Element;
            SLAAggregation: (row?: any, column?: any) => JSX.Element;
            actions: (row?: any, column?: any) => JSX.Element;
        };
        const newRowRenderers: ColumnRendererMap  = {
            PepwiseProject: () => renderNewPepwiseProject(),
            SLAType: () => renderNewSLAType(),
            Days: () => renderNewDays(),
            SLADate: () => renderNewSLADate(),
            SLATime: () => renderNewSLATime(),
            SLACriteria: () => renderNewSLACriteria(),
            SLAAggregation: () => renderNewSLAAggregation(),
            actions: () => renderNewActions(),
        };

        const editRowRenderers: ColumnRendererMap = {
            PepwiseProject: (row, column) => renderEditPepwiseProject(row, column),
            SLAType: (row) => renderEditSLAType(row),
            Days: (row) => renderEditDays(row),
            SLADate: (row) => renderEditSLADate(row),
            SLATime: (row) => renderEditSLATime(row),
            SLACriteria: (row) => renderEditSLACriteria(row),
            SLAAggregation: (row) => renderEditSLAAggregation(row),
            actions: (row) => renderEditActions(row),
        };

        const viewRowRenderers: Record<string, (row: TableRow) => JSX.Element> = {
            PepwiseProject: (row) => renderViewPepwiseProject(row),
            SLAType: (row) => renderViewSLAType(row),
            Days: (row) => renderViewDays(row),
            SLADate: (row) => renderViewSLADate(row),
            SLATime: (row) => renderViewSLATime(row),
            SLACriteria: (row) => renderViewSLACriteria(row),
            SLAAggregation: (row) => renderViewSLAAggregation(row),
            actions: (row) => renderViewActions(row)
        };

        return columnNames.map((col: any) => ({
            key: col.name,
            name: col.label,
            editable: () => col.isEditable,
            ...(col.name === 'PepwiseProject' && { width: 200, minWidth: 160, resizable: true }),
            ...(col.name === 'SLAType' && { width: 180, minWidth: 120, resizable: true }),
            ...(col.name === 'Days' && { width: 220, minWidth: 160, resizable: true }),
            ...(col.name === 'SLATime' && { width: 200, minWidth: 140, resizable: true }),
            formatter({ row, column }: any) {
                const colName = column.key;

                // NEW MODE
                if (row.isNew && colName in newRowRenderers) {
                    return newRowRenderers[colName as keyof ColumnRendererMap]();
                }

                // EDIT MODE
                if (row.isEditing && colName in editRowRenderers) {
                    return editRowRenderers[colName as keyof ColumnRendererMap](row, column);
                }

                // --- VIEW MODE ---
                if (!row.isNew && !row.isEditing && colName in viewRowRenderers) {
                    return viewRowRenderers[colName](row);
                }
            
                return null;
            },
        }));
    };

    function isAllDaysSelectedForProject(projectName: string, selectedRows: TableRow[]) {
        const usedDays = getWeeklyUsedDaysMap(selectedRows)[projectName] || [];
        const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return allDays.every(day => usedDays.includes(day));
    }

    const filteredProjectOptions = projectOptions?.filter(opt => {
        const projectRows = selectedRows && selectedRows.filter(row => row.PepwiseProject === opt.label);
        if (projectRows.length === 0) return true;

        // Exclude if project already has a Weekly SLA for "All Days"
        const hasWeeklyAllDays = projectRows.some(
            row => row.SLAType === 'Weekly' && (
                row.Days === 'All Days' ||
                row.Days === 'Mon, Tue, Wed, Thu, Fri, Sat, Sun'
            )
        );
        if (hasWeeklyAllDays) return false;

        // If project has weekly, but not all days, allow
        const isWeekly = projectRows.some(row => row.SLAType === 'Weekly');
        if (isWeekly) {
            return !isAllDaysSelectedForProject(opt.label, selectedRows);
        }
        // For daily, only one row allowed
        return false;
    });

    return (
        <Box className={"main-bordered-container"} sx={{ mt: 3, padding: '20px', borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ opacity: 0.7, mb: 2, px: 2, color: 'textLight.main',display:'flex',gap:'4px' }}>
                <img src={info} alt=""/> This section is to configure SLA.
            </Typography>
            <Grid item xs={12}>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', mb: '16px', mt: '26px', gap: '12px' }}
                >
                    <Typography
                        variant="subHeader"
                        sx={{
                            color: '#009BD8',
                            pl:'16px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        SLA Configurations
                    </Typography>
                    <Tooltip title="" placement="top">
                        <Box component="img" src={infoDim} alt="info" sx={{ width: 20, height: 20, cursor: 'pointer' }} />
                    </Tooltip>
                    <Divider sx={{ flexGrow: 1, borderColor: '#38bdf8' }} />
                </Box>
            </Grid>
            {tableLoading ? (
                <Stack justifyContent="center" alignItems="center" height="250px">
                    <LoaderComponent/>
                </Stack>
            ) : (
                <Box px={2}>
                    <CustomizedDataGrid
                        title="Configure Project Level SLA"
                        gridRef={gridRef}
                        tableColumns={getColumnsSLA()}
                        tableData={selectedRows}
                        defaultColumnNames={defaultColumnNamesSLA}
                        autoSetDraggedColumns={false}
                        isRenderPagination={false}
                        isShowMoreButton={false}
                        idleFilterIcon={IdleFilterBlueIcon}
                        style={{ maxHeight: '250px' }}
                        rowHeight={(row: any) => (row.row.isNew || row.row.isEditing) ? 50 : 40}
                        isAllocationModalSLA
                    />
                    <Stack>
                        <Button
                            variant="text"
                            sx={{ width: '8rem', fontSize: '15px', color: 'text.focus' }}
                            onClick={handleAddNewRow}
                            disabled={
                        isNewAllocRowExists ||
                        projectOptions?.every((opt: any) => {
                            const projectRows = selectedRows && selectedRows.filter(row => row.PepwiseProject === opt.label);
                            if (projectRows?.length === 0) return false; // Not configured, can add
                            // If any daily SLA exists, project is fully configured
                            if (projectRows.some(row => row.SLAType === 'Daily')) return true;
                            // For weekly, check if all 7 days are covered
                            const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                            const coveredDays = new Set(
                                projectRows
                                    .filter((row: any) => row.SLAType === 'Weekly')
                                    .flatMap((row: any) =>
                                        row.Days === 'All Days'
                                            ? allDays
                                            : row.Days.split(',').map((d: any) => d.trim())
                                    )
                            );
                            return allDays?.every(day => coveredDays.has(day));
                        })
                            }
                        >
                            + Add Row
                        </Button>
                    </Stack>
                </Box>
            )}

            <Box px={2} sx={{ mt: 4 }}>
                {jobLevelTableLoading ? (
                        <Stack justifyContent="center" alignItems="center" height="250px">
                            <Typography variant="pageTitle" color="dark.main">
                                Loading...
                            </Typography>
                        </Stack>
                    ) : (
                        <Box sx={getSLAGridCustomSx()}>
                            <CustomizedDataGrid
                                title="Job Level SLA"
                                gridRef={jobLevelGridRef}
                                tableColumns={getJobLevelColumnsSLA()}
                                tableData={jobRows}
                                defaultColumnNames={jobLevelDefaultColumnNamesSLA}
                                autoSetDraggedColumns={false}
                                isRenderPagination={false}
                                idleFilterIcon={IdleFilterBlueIcon}
                                isShowMoreButton={false}
                                style={{ height: 'auto', maxHeight: '250px', minHeight: '60px' }}                                rowHeight={(row: any) => (row.row.isNew || row.row.isEditing) ? 50 : 40}
                                isAllocationModalSLA
                            />
                        {canShowAddJobLevelRowButton && (
                            <Stack sx={{ mt: '4px' }}>
                                <Button
                                    variant="text"
                                    sx={{ width: '8rem', fontSize: '15px', color: 'text.focus' }}
                                    onClick={handleAddNewJobLevelRow}
                                >
                                    + Add Row
                                </Button>
                            </Stack>
                        )}
                        </Box>
                    )
                }
            </Box>
            <SLAConfigurationModal
                open={isSLAConfigModalOpen}
                onClose={handleCloseSLAConfigModal}
                onSave={handleSaveSLAConfigModal}
                selectedProject={configModalProject}
                initialData={configModalInitialData}
                isViewOnly={isSLAConfigViewOnly}
            />
            <Dialog open={openModal.isOpen} onClose={handleModalClose} fullWidth={true} maxWidth="md">
                <DataPipelineModal
                    handleEditClose={handleModalClose}
                    programData={openModal}
                    getToastData={getToastData}
                    passInfoToParent={passInfoToParent}
                />
            </Dialog>
        </Box>
    )
}
export default SLATab;

